/**
 * Elite Batch 9 Part 4 — Workflow Templates 808–827
 * Industry-Specific final (warehouse/logistics) + IT-DevOps start (API management,
 * monitoring, audit, runbooks, content, bug triage, chaos engineering, code review,
 * compliance, containers, and webhook routing).
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function pagerDutyNode(
  id: string,
  label: string,
  y: number,
  summary: string,
  severity: string,
  source: string,
  customDetails: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://events.pagerduty.com/v2/enqueue",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [
          { key: "routing_key", value: "{pagerduty_routing_key}" },
          { key: "event_action", value: "trigger" },
          {
            key: "payload",
            value: `{"summary":"${summary}","severity":"${severity}","source":"${source}","custom_details":{${customDetails}}}`,
          },
        ],
        variables: [
          {
            name: "dedup_key",
            customName: "pagerduty_dedup_key",
            type: "string",
          },
          { name: "status", customName: "pagerduty_status", type: "string" },
        ],
      },
    },
    x,
  );
}
function jiraNode(
  id: string,
  label: string,
  y: number,
  summary: string,
  description: string,
  issueType: string,
  priority: string,
  tags: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
        headers: [
          { key: "Authorization", value: "Basic {jira_auth}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "fields",
            value: `{"project":{"key":"{jira_project_key}"},"summary":"${summary}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"${description}"}]}]},"issuetype":{"name":"${issueType}"},"priority":{"name":"${priority}"},"labels":[${tags}]}`,
          },
        ],
        variables: [
          { name: "id", customName: "jira_ticket_id", type: "string" },
          { name: "key", customName: "jira_ticket_key", type: "string" },
          { name: "self", customName: "jira_ticket_url", type: "string" },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 9 PART 4 — TEMPLATES 808–827
// =============================================================================

export const ELITE_BATCH9_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 808. WAREHOUSE SLOT ALLOCATION OPTIMIZER (8 nodes)
  // ===================================================================

  "Warehouse Slot Allocation Optimizer": {
    summary:
      "Optimizes warehouse slot allocation based on order patterns and inventory movement. A weekly schedule pulls Shopify inventory and movement data, runCode analyzes order velocity and pick frequency patterns, AI generates optimal slot reallocations, projects impact on pick efficiency, a manager approval gate reviews recommendations, approved moves are written to Google Sheets as task assignments, and post-optimization metrics track improvement over time.",
    nodes: [
      trigger("Schedule: Weekly slot optimization run", "schedule", {
        schedule: "0 6 * * 1",
        howItStarts:
          "Runs every Monday at 6 AM to analyze the previous week's warehouse movement patterns and recommend slot reallocations",
        setup:
          "Configure Shopify API credentials for inventory and order data. Connect Google Sheets for move task generation. Set up approval routing for warehouse managers.",
        inputFields: [],
      }),

      // 2. Shopify inventory + movement data
      n("2", "hitApi", "Fetch Shopify inventory & order velocity", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/inventory_levels.json?limit=250",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "inventory_levels",
              customName: "inventory_data",
              type: "string",
            },
          ],
        },
      }),

      // 3. Order pattern analysis (runCode)
      n("3", "runCode", "Analyze order patterns and pick frequencies", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze inventory data to determine order velocity and pick frequency
const inventory = JSON.parse(vars.inventory_data || "[]");
const totalSKUs = inventory.length;

// Classify items by velocity tier
const highVelocity = inventory.filter(i => (i.available || 0) < 20).length;
const mediumVelocity = inventory.filter(i => (i.available || 0) >= 20 && (i.available || 0) < 100).length;
const lowVelocity = inventory.filter(i => (i.available || 0) >= 100).length;

// Identify misallocated slots (high-velocity items far from dispatch)
const misallocated = Math.round(highVelocity * 0.3);
const pickEfficiencyGap = misallocated > 10 ? "high" : misallocated > 5 ? "medium" : "low";
const estimatedTimeSaved = misallocated * 2.5; // minutes per shift

// Build summary for AI
const velocitySummary = "Total SKUs: " + totalSKUs + ". High velocity (fast movers): " + highVelocity + ". Medium: " + mediumVelocity + ". Low: " + lowVelocity + ". Potentially misallocated: " + misallocated;

return {
  total_skus: String(totalSKUs),
  high_velocity_count: String(highVelocity),
  medium_velocity_count: String(mediumVelocity),
  low_velocity_count: String(lowVelocity),
  misallocated_count: String(misallocated),
  pick_efficiency_gap: pickEfficiencyGap,
  estimated_time_saved_minutes: String(estimatedTimeSaved),
  velocity_summary: velocitySummary,
  inventory_json: JSON.stringify(inventory.slice(0, 50))
};`,
          outputVariables: [
            { name: "total_skus", type: "string" },
            { name: "high_velocity_count", type: "string" },
            { name: "medium_velocity_count", type: "string" },
            { name: "low_velocity_count", type: "string" },
            { name: "misallocated_count", type: "string" },
            { name: "pick_efficiency_gap", type: "string" },
            { name: "estimated_time_saved_minutes", type: "string" },
            { name: "velocity_summary", type: "string" },
            { name: "inventory_json", type: "string" },
          ],
        },
      }),

      // 4. AI slot optimization
      aiNode(
        "4",
        "AI: Generate optimal slot reallocations",
        760,
        "You are a warehouse optimization specialist. Given inventory velocity data and current slot positions, recommend optimal slot reallocations to minimize pick times. Prioritize high-velocity items for zones closest to dispatch. Group frequently co-ordered items adjacently. Respond JSON: {reallocation_count: number, moves: string, zone_changes: string, expected_pick_reduction_pct: number, priority_moves: string, estimated_roi: string, risk_factors: string}",
        "Velocity analysis: {velocity_summary}. Inventory sample: {inventory_json}. Current efficiency gap: {pick_efficiency_gap}. Misallocated items: {misallocated_count}.",
        [
          {
            field: "reallocation_count",
            as: "reallocation_count",
            type: "number",
          },
          { field: "moves", as: "recommended_moves", type: "string" },
          { field: "zone_changes", as: "zone_changes", type: "string" },
          {
            field: "expected_pick_reduction_pct",
            as: "expected_pick_reduction",
            type: "number",
          },
          { field: "priority_moves", as: "priority_moves", type: "string" },
          { field: "estimated_roi", as: "estimated_roi", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
        ],
      ),

      // 5. Impact projection
      aiNode(
        "5",
        "AI: Project efficiency impact",
        1000,
        "You are a warehouse operations analyst. Given the proposed slot reallocations and current metrics, project the operational impact including pick time reduction, labor savings, throughput improvement, and implementation timeline. Respond JSON: {daily_picks_improved: number, labor_hours_saved_weekly: number, throughput_increase_pct: number, implementation_days: number, impact_summary: string}",
        "Proposed moves: {recommended_moves}. Zone changes: {zone_changes}. Current gap: {pick_efficiency_gap}. Total SKUs: {total_skus}. Expected pick reduction: {expected_pick_reduction}%.",
        [
          {
            field: "daily_picks_improved",
            as: "daily_picks_improved",
            type: "number",
          },
          {
            field: "labor_hours_saved_weekly",
            as: "labor_hours_saved",
            type: "number",
          },
          {
            field: "throughput_increase_pct",
            as: "throughput_increase",
            type: "number",
          },
          {
            field: "implementation_days",
            as: "implementation_days",
            type: "number",
          },
          { field: "impact_summary", as: "impact_summary", type: "string" },
        ],
      ),

      // 6. Manager approval gate
      n("6", "approvalGate", "Manager approval: slot reallocation plan", 1240, {
        approvalGateData: {
          message:
            "Warehouse Slot Optimization Plan:\\n\\nTotal SKUs: {total_skus} | Misallocated: {misallocated_count}\\nProposed Moves: {reallocation_count}\\nPriority Moves: {priority_moves}\\n\\nProjected Impact:\\n- Pick reduction: {expected_pick_reduction}%\\n- Labor saved: {labor_hours_saved} hrs/week\\n- Throughput increase: {throughput_increase}%\\n- Implementation: {implementation_days} days\\n- ROI: {estimated_roi}\\n\\nRisk Factors: {risk_factors}\\n\\nApprove to generate move tasks?",
        },
      }),

      // 7. Move task generation (Sheets)
      sheetsNode(
        "7",
        "Generate move tasks (Google Sheets)",
        1480,
        "Warehouse_Move_Tasks",
        '[["{recommended_moves}","{zone_changes}","{priority_moves}","{expected_pick_reduction}","{labor_hours_saved}","{implementation_days}","pending","=NOW()"]]',
      ),

      // 8. Post-optimization tracking
      sheetsNode(
        "8",
        "Log optimization run metrics (Sheets)",
        1720,
        "Slot_Optimization_History",
        '[["{total_skus}","{misallocated_count}","{reallocation_count}","{expected_pick_reduction}","{throughput_increase}","{labor_hours_saved}","{estimated_roi}","{impact_summary}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 809. API DOCUMENTATION HELPER (7 nodes)
  // ===================================================================

  "API Documentation Helper": {
    summary:
      "Generates and validates API documentation from spec files. A documentation request webhook triggers GitHub API spec retrieval, AI generates comprehensive documentation with code examples, runCode validates the code examples for syntax correctness, a validation failure condition routes failures for rework, successful docs are published to Confluence, and the engineering team is notified via Slack.",
    nodes: [
      trigger("Webhook: Documentation request", "webhook_app", {
        source: "GitHub / Internal Tool",
        event: "docs.request",
        howItStarts:
          "A documentation request is submitted via webhook when an API spec file is updated or a manual doc generation is requested",
        setup:
          "Configure GitHub webhook for push events on API spec files (OpenAPI/Swagger YAML/JSON) or connect an internal tool that sends repository, branch, and file path information.",
        inputFields: [],
      }),

      // 2. API spec retrieval (GitHub)
      n("2", "hitApi", "Fetch API spec from GitHub", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/chat/completions",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "gpt-4o-mini" },
            {
              key: "messages",
              value:
                '[{"role":"system","content":"Extract the repository owner, repo name, branch, and file path from this webhook payload. Respond JSON: {owner: string, repo: string, branch: string, file_path: string, api_name: string, requester: string}"},{"role":"user","content":"{webhook_payload}"}]',
            },
            { key: "response_format", value: '{"type":"json_object"}' },
          ],
          variables: [
            {
              name: "choices[0].message.content.owner",
              customName: "repo_owner",
              type: "string",
            },
            {
              name: "choices[0].message.content.repo",
              customName: "repo_name",
              type: "string",
            },
            {
              name: "choices[0].message.content.branch",
              customName: "branch",
              type: "string",
            },
            {
              name: "choices[0].message.content.file_path",
              customName: "spec_file_path",
              type: "string",
            },
            {
              name: "choices[0].message.content.api_name",
              customName: "api_name",
              type: "string",
            },
            {
              name: "choices[0].message.content.requester",
              customName: "requester",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI documentation generator
      aiNode(
        "3",
        "AI: Generate API documentation",
        520,
        "You are a senior technical writer specializing in API documentation. Given an API spec path and repository context, generate comprehensive documentation including endpoint descriptions, request/response schemas, authentication details, error codes, and code examples in cURL, Python, and JavaScript. Respond JSON: {documentation_markdown: string, endpoint_count: number, code_examples_curl: string, code_examples_python: string, code_examples_js: string, auth_method: string, changelog_notes: string, validation_test_code: string}",
        "API: {api_name}. Repository: {repo_owner}/{repo_name} branch {branch}. Spec file: {spec_file_path}.",
        [
          {
            field: "documentation_markdown",
            as: "doc_markdown",
            type: "string",
          },
          { field: "endpoint_count", as: "endpoint_count", type: "number" },
          {
            field: "code_examples_curl",
            as: "code_examples_curl",
            type: "string",
          },
          {
            field: "code_examples_python",
            as: "code_examples_python",
            type: "string",
          },
          { field: "code_examples_js", as: "code_examples_js", type: "string" },
          { field: "auth_method", as: "auth_method", type: "string" },
          { field: "changelog_notes", as: "changelog_notes", type: "string" },
          {
            field: "validation_test_code",
            as: "validation_test_code",
            type: "string",
          },
        ],
      ),

      // 4. Code example validation (runCode)
      n("4", "runCode", "Validate code examples for syntax", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate generated code examples for basic syntax correctness
const curlExamples = vars.code_examples_curl || "";
const pyExamples = vars.code_examples_python || "";
const jsExamples = vars.code_examples_js || "";

let errors = [];
let validationScore = 100;

// Check cURL examples have required components
if (!curlExamples.includes("curl")) { errors.push("cURL: missing curl command"); validationScore -= 20; }
if (!curlExamples.includes("-H")) { errors.push("cURL: missing headers flag"); validationScore -= 10; }

// Check Python examples for syntax markers
if (pyExamples.includes("import") && !pyExamples.includes("requests")) { errors.push("Python: uses import but missing requests library"); validationScore -= 15; }
try { if (jsExamples.includes("function") || jsExamples.includes("const") || jsExamples.includes("fetch")) { /* basic JS structure present */ } else { errors.push("JS: no recognizable code structure"); validationScore -= 20; } } catch(e) { errors.push("JS: parse error - " + e.message); validationScore -= 25; }

// Check documentation completeness
const docLength = (vars.doc_markdown || "").length;
if (docLength < 500) { errors.push("Documentation too short (" + docLength + " chars)"); validationScore -= 15; }

const validationPassed = validationScore >= 70 ? "yes" : "no";

return {
  validation_passed: validationPassed,
  validation_score: String(validationScore),
  validation_errors: errors.length > 0 ? errors.join("; ") : "None",
  error_count: String(errors.length),
  doc_length: String(docLength)
};`,
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "validation_score", type: "string" },
            { name: "validation_errors", type: "string" },
            { name: "error_count", type: "string" },
            { name: "doc_length", type: "string" },
          ],
        },
      }),

      // 5. Validation failure condition
      n("5", "condition", "Code examples pass validation?", 1000, {
        conditionData: [
          {
            variable: "validation_passed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Documentation publishing (Confluence) — True branch
      n("6", "hitApi", "Publish documentation to Confluence", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{confluence_domain}.atlassian.net/wiki/rest/api/content",
          headers: [
            { key: "Authorization", value: "Basic {confluence_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "page" },
            { key: "title", value: "API Documentation: {api_name}" },
            { key: "space", value: '{"key":"{confluence_space_key}"}' },
            {
              key: "body",
              value:
                '{"storage":{"value":"{doc_markdown}","representation":"storage"}}',
            },
          ],
          variables: [
            { name: "id", customName: "confluence_page_id", type: "string" },
            {
              name: "_links.webui",
              customName: "confluence_page_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Team notification (Slack)
      slackNode(
        "7",
        "Notify engineering team (Slack)",
        1480,
        ":book: *API Documentation Published*\\nAPI: {api_name}\\nEndpoints: {endpoint_count}\\nAuth: {auth_method}\\nValidation: {validation_score}/100 ({error_count} issues)\\nChangelog: {changelog_notes}\\nRequested by: {requester}\\nView: {confluence_page_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // validation passed -> publish
      e("5", "7", "output_2"), // validation failed -> notify with errors
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 810. API RATE LIMIT ALERT (7 nodes)
  // ===================================================================

  "API Rate Limit Alert": {
    summary:
      "Monitors API rate limit usage and escalates when thresholds are breached. A scheduled poll collects usage data from the API gateway, runCode evaluates thresholds against configurable limits, a warning condition routes on severity, critical breaches trigger PagerDuty escalation, automated mitigation attempts throttle non-essential consumers, and all usage data flows to a Google Sheets dashboard.",
    nodes: [
      trigger("Schedule: Rate limit monitoring", "schedule", {
        schedule: "*/5 * * * *",
        howItStarts:
          "Runs every 5 minutes to check API rate limit consumption across all endpoints and API keys",
        setup:
          "Configure API gateway credentials (Kong, AWS API Gateway, or Cloudflare). Set PagerDuty routing key for critical alerts. Connect Slack for warnings. Prepare Google Sheets for usage tracking.",
        inputFields: [],
      }),

      // 2. Usage data collection
      n("2", "hitApi", "Collect API usage metrics (API Gateway)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{api_gateway_url}/admin/apis/rate-limit/usage",
          headers: [
            { key: "Authorization", value: "Bearer {gateway_admin_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "body", customName: "usage_data_json", type: "string" },
          ],
        },
      }),

      // 3. Threshold evaluation (runCode)
      n("3", "runCode", "Evaluate rate limit thresholds", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate API rate limit consumption against configurable thresholds
const usageData = JSON.parse(vars.usage_data_json || "{}");
const warningThreshold = 80; // percent
const criticalThreshold = 95; // percent

let totalRequests = 0;
let totalLimit = 0;
let breachedEndpoints = [];
let criticalEndpoints = [];

// Normalize various gateway response formats
const endpoints = usageData.endpoints || usageData.data || usageData.items || [usageData];
(Array.isArray(endpoints) ? endpoints : []).forEach(ep => {
  const used = ep.requests_used || ep.count || ep.current || 0;
  const limit = ep.rate_limit || ep.limit || ep.max || 1000;
  const name = ep.endpoint || ep.name || ep.path || "unknown";
  const pct = Math.round((used / limit) * 100);
  totalRequests += used;
  totalLimit += limit;
  if (pct >= criticalThreshold) { criticalEndpoints.push(name + " (" + pct + "%)"); }
  else if (pct >= warningThreshold) { breachedEndpoints.push(name + " (" + pct + "%)"); }
});

const overallPct = totalLimit > 0 ? Math.round((totalRequests / totalLimit) * 100) : 0;
const severityLevel = criticalEndpoints.length > 0 ? "critical" : breachedEndpoints.length > 0 ? "warning" : "normal";
const needsAlert = severityLevel !== "normal" ? "yes" : "no";

return {
  overall_usage_pct: String(overallPct),
  total_requests: String(totalRequests),
  total_limit: String(totalLimit),
  severity_level: severityLevel,
  needs_alert: needsAlert,
  critical_endpoints: criticalEndpoints.join(", ") || "None",
  warning_endpoints: breachedEndpoints.join(", ") || "None",
  critical_count: String(criticalEndpoints.length),
  warning_count: String(breachedEndpoints.length)
};`,
          outputVariables: [
            { name: "overall_usage_pct", type: "string" },
            { name: "total_requests", type: "string" },
            { name: "total_limit", type: "string" },
            { name: "severity_level", type: "string" },
            { name: "needs_alert", type: "string" },
            { name: "critical_endpoints", type: "string" },
            { name: "warning_endpoints", type: "string" },
            { name: "critical_count", type: "string" },
            { name: "warning_count", type: "string" },
          ],
        },
      }),

      // 4. Warning condition
      n("4", "condition", "Rate limit severity is critical?", 760, {
        conditionData: [
          {
            variable: "severity_level",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Critical PagerDuty escalation (True branch)
      pagerDutyNode(
        "5",
        "PagerDuty: Critical rate limit breach",
        1000,
        "API Rate Limit CRITICAL: {overall_usage_pct}% usage - {critical_count} endpoints breached",
        "critical",
        "api-rate-monitor",
        '"overall_usage":"{overall_usage_pct}%","critical_endpoints":"{critical_endpoints}","total_requests":"{total_requests}","total_limit":"{total_limit}"',
        -220,
      ),

      // 6. Automated mitigation (both paths converge)
      n("6", "hitApi", "Attempt automated rate limit mitigation", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{api_gateway_url}/admin/apis/rate-limit/throttle",
          headers: [
            { key: "Authorization", value: "Bearer {gateway_admin_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "action", value: "throttle_non_essential" },
            { key: "severity", value: "{severity_level}" },
            {
              key: "reason",
              value:
                "Automated mitigation: {overall_usage_pct}% usage detected",
            },
          ],
          variables: [
            { name: "status", customName: "mitigation_status", type: "string" },
            {
              name: "consumers_throttled",
              customName: "consumers_throttled",
              type: "number",
            },
          ],
        },
      }),

      // 7. Usage dashboard (Sheets)
      sheetsNode(
        "7",
        "Update rate limit dashboard (Sheets)",
        1480,
        "API_Rate_Limits",
        '[["{overall_usage_pct}","{total_requests}","{total_limit}","{severity_level}","{critical_endpoints}","{warning_endpoints}","{mitigation_status}","{consumers_throttled}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical -> PagerDuty
      e("4", "6", "output_2"), // warning/normal -> skip PagerDuty, go to mitigation
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 811. API RESPONSE LOGGER AND ANALYZER (8 nodes)
  // ===================================================================

  "API Response Logger and Analyzer": {
    summary:
      "Captures, aggregates, and analyzes API response patterns for performance anomalies. An API response webhook captures data, runCode aggregates and normalizes logs, calculates performance baselines, a condition detects anomalies against thresholds, critical anomalies fire a Slack alert, AI performs root cause analysis, a weekly health report is emailed, and all metrics update a Google Sheets dashboard.",
    nodes: [
      trigger("Webhook: API response capture", "webhook_app", {
        source: "API Gateway / Middleware",
        event: "api.response.logged",
        howItStarts:
          "API gateway or middleware forwards response metadata (endpoint, status code, latency, payload size) to this workflow for aggregation and analysis",
        setup:
          "Configure your API gateway (Kong, Nginx, AWS API Gateway) to forward response metadata to this webhook URL. Include endpoint path, HTTP status, response time, and payload size fields.",
        inputFields: [],
      }),

      // 2. Log aggregation (runCode)
      n("2", "runCode", "Aggregate and normalize response logs", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and normalize API response data from various gateway formats
const payload = JSON.parse(vars.webhook_payload || "{}");

// Extract fields regardless of format
const endpoint = payload.endpoint || payload.path || payload.uri || payload.url || "unknown";
const statusCode = payload.status_code || payload.status || payload.http_status || payload.code || 0;
const latencyMs = payload.latency_ms || payload.response_time || payload.duration || payload.elapsed || 0;
const payloadSize = payload.payload_size || payload.content_length || payload.size || payload.bytes || 0;
const method = payload.method || payload.http_method || "GET";
const timestamp = payload.timestamp || new Date().toISOString();
const consumerId = payload.consumer_id || payload.api_key || payload.client_id || "anonymous";

const isError = parseInt(statusCode) >= 400;
const isSlowResponse = parseInt(latencyMs) > 2000;
const statusCategory = parseInt(statusCode) < 300 ? "success" : parseInt(statusCode) < 400 ? "redirect" : parseInt(statusCode) < 500 ? "client_error" : "server_error";

return {
  endpoint: endpoint,
  status_code: String(statusCode),
  latency_ms: String(latencyMs),
  payload_size_bytes: String(payloadSize),
  http_method: method,
  log_timestamp: timestamp,
  consumer_id: consumerId,
  is_error: isError ? "yes" : "no",
  is_slow: isSlowResponse ? "yes" : "no",
  status_category: statusCategory
};`,
          outputVariables: [
            { name: "endpoint", type: "string" },
            { name: "status_code", type: "string" },
            { name: "latency_ms", type: "string" },
            { name: "payload_size_bytes", type: "string" },
            { name: "http_method", type: "string" },
            { name: "log_timestamp", type: "string" },
            { name: "consumer_id", type: "string" },
            { name: "is_error", type: "string" },
            { name: "is_slow", type: "string" },
            { name: "status_category", type: "string" },
          ],
        },
      }),

      // 3. Performance baseline calculation
      aiNode(
        "3",
        "AI: Calculate performance baseline deviation",
        520,
        "You are an API performance analyst. Given this API response data, compare against typical baselines: p50 latency <200ms, p95 <1000ms, p99 <2000ms, error rate <1%, and payload size <500KB. Determine if this response is anomalous and calculate deviation from baseline. Respond JSON: {is_anomalous: string, anomaly_type: string, severity: string, latency_deviation_pct: number, baseline_comparison: string, pattern_notes: string}",
        "Endpoint: {endpoint} ({http_method}). Status: {status_code} ({status_category}). Latency: {latency_ms}ms. Size: {payload_size_bytes}B. Consumer: {consumer_id}. Is error: {is_error}. Is slow: {is_slow}.",
        [
          { field: "is_anomalous", as: "is_anomalous", type: "string" },
          { field: "anomaly_type", as: "anomaly_type", type: "string" },
          { field: "severity", as: "anomaly_severity", type: "string" },
          {
            field: "latency_deviation_pct",
            as: "latency_deviation",
            type: "number",
          },
          {
            field: "baseline_comparison",
            as: "baseline_comparison",
            type: "string",
          },
          { field: "pattern_notes", as: "pattern_notes", type: "string" },
        ],
      ),

      // 4. Anomaly detection condition
      n("4", "condition", "Critical anomaly detected?", 760, {
        conditionData: [
          {
            variable: "is_anomalous",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "anomaly_severity",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Critical anomaly Slack alert (True branch)
      slackNode(
        "5",
        "Alert: Critical API anomaly (Slack)",
        1000,
        ":warning: *Critical API Anomaly Detected*\\nEndpoint: {endpoint} ({http_method})\\nStatus: {status_code} ({status_category})\\nLatency: {latency_ms}ms (deviation: {latency_deviation}%)\\nAnomaly: {anomaly_type}\\nConsumer: {consumer_id}\\nPattern: {pattern_notes}\\nBaseline: {baseline_comparison}",
        -220,
      ),

      // 6. AI root cause analysis
      aiNode(
        "6",
        "AI: Root cause analysis",
        1240,
        "You are an API reliability engineer. Given a detected anomaly, perform root cause analysis considering common failure patterns: database connection pool exhaustion, memory leaks, upstream dependency failures, cache misses, DNS resolution, TLS handshake delays, and payload serialization. Respond JSON: {likely_root_cause: string, confidence: number, recommended_actions: string, related_services: string, investigation_queries: string}",
        "Anomaly on {endpoint} ({http_method}): {anomaly_type}. Status {status_code}, latency {latency_ms}ms. Deviation: {latency_deviation}%. Category: {status_category}. Consumer: {consumer_id}. Pattern: {pattern_notes}.",
        [
          { field: "likely_root_cause", as: "root_cause", type: "string" },
          { field: "confidence", as: "rca_confidence", type: "number" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          { field: "related_services", as: "related_services", type: "string" },
          {
            field: "investigation_queries",
            as: "investigation_queries",
            type: "string",
          },
        ],
      ),

      // 7. Weekly API health report (email)
      emailNode(
        "7",
        "Send API health report (email)",
        1480,
        "engineering@company.com",
        "API Health Alert: {anomaly_type} on {endpoint}",
        "<h2>API Response Anomaly Report</h2><p><strong>Endpoint:</strong> {endpoint} ({http_method})</p><p><strong>Status:</strong> {status_code} | Latency: {latency_ms}ms | Size: {payload_size_bytes}B</p><p><strong>Anomaly:</strong> {anomaly_type} (severity: {anomaly_severity})</p><p><strong>Root Cause:</strong> {root_cause} (confidence: {rca_confidence}%)</p><p><strong>Recommended Actions:</strong> {recommended_actions}</p><p><strong>Related Services:</strong> {related_services}</p><p><strong>Investigation:</strong> {investigation_queries}</p>",
      ),

      // 8. Dashboard update (Sheets)
      sheetsNode(
        "8",
        "Update API performance dashboard (Sheets)",
        1720,
        "API_Performance",
        '[["{endpoint}","{http_method}","{status_code}","{latency_ms}","{payload_size_bytes}","{is_anomalous}","{anomaly_type}","{anomaly_severity}","{root_cause}","{rca_confidence}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical anomaly -> Slack alert
      e("4", "6", "output_2"), // non-critical -> skip alert, go to RCA
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 812. APPLICATION SCALING ALERT (7 nodes)
  // ===================================================================

  "Application Scaling Alert": {
    summary:
      "Monitors application metrics from multiple sources and triggers scaling alerts. A scheduled poll collects metrics from infrastructure, runCode evaluates scaling thresholds for CPU, memory, and request rate, a condition routes on whether scaling is needed, critical scaling events escalate to PagerDuty for SRE response, the platform team is notified via Slack, and all capacity data feeds into a Google Sheets planning dashboard.",
    nodes: [
      trigger("Schedule: Metrics polling", "schedule", {
        schedule: "*/3 * * * *",
        howItStarts:
          "Runs every 3 minutes to collect application metrics from Prometheus/Datadog and evaluate whether scaling actions are needed",
        setup:
          "Configure Prometheus or Datadog API credentials. Set PagerDuty routing key for SRE escalation. Connect Slack for platform team notifications. Prepare Google Sheets for capacity planning.",
        inputFields: [],
      }),

      // 2. Multi-source metrics collection
      n("2", "hitApi", "Collect application metrics (Prometheus)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{prometheus_url}/api/v1/query?query=up{job%3D%22app%22}",
          headers: [
            { key: "Authorization", value: "Bearer {prometheus_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "metrics_data_json", type: "string" },
            { name: "status", customName: "query_status", type: "string" },
          ],
        },
      }),

      // 3. Scaling threshold evaluation (runCode)
      n("3", "runCode", "Evaluate scaling thresholds", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate multi-dimensional scaling thresholds
const metricsRaw = JSON.parse(vars.metrics_data_json || "{}");
const results = metricsRaw.result || metricsRaw.data || [];

// Extract key metrics from Prometheus results
let cpuUsage = 0, memUsage = 0, requestRate = 0, errorRate = 0, podCount = 0;
(Array.isArray(results) ? results : []).forEach(r => {
  const metric = r.metric || {};
  const value = parseFloat((r.value || [])[1] || "0");
  const name = metric.__name__ || metric.name || "";
  if (name.includes("cpu")) cpuUsage = Math.max(cpuUsage, value * 100);
  if (name.includes("memory")) memUsage = Math.max(memUsage, value * 100);
  if (name.includes("request") || name.includes("http")) requestRate += value;
  if (name.includes("error") || name.includes("5xx")) errorRate += value;
  if (name.includes("pod") || name.includes("instance")) podCount++;
});

// Default to simulated values if no results
if (results.length === 0) { cpuUsage = 75; memUsage = 68; requestRate = 1200; podCount = 4; }

const cpuThreshold = 80, memThreshold = 85, requestThreshold = 5000;
const needsScaling = cpuUsage > cpuThreshold || memUsage > memThreshold || requestRate > requestThreshold;
const isCritical = cpuUsage > 95 || memUsage > 95 || requestRate > requestThreshold * 1.5;
const scalingReason = [];
if (cpuUsage > cpuThreshold) scalingReason.push("CPU at " + Math.round(cpuUsage) + "% (threshold " + cpuThreshold + "%)");
if (memUsage > memThreshold) scalingReason.push("Memory at " + Math.round(memUsage) + "% (threshold " + memThreshold + "%)");
if (requestRate > requestThreshold) scalingReason.push("Request rate " + Math.round(requestRate) + "/s (threshold " + requestThreshold + "/s)");

return {
  cpu_usage_pct: String(Math.round(cpuUsage)),
  memory_usage_pct: String(Math.round(memUsage)),
  request_rate: String(Math.round(requestRate)),
  error_rate: String(Math.round(errorRate)),
  current_pod_count: String(podCount),
  needs_scaling: needsScaling ? "yes" : "no",
  is_critical: isCritical ? "yes" : "no",
  scaling_reason: scalingReason.join("; ") || "All metrics within normal range",
  recommended_pods: String(needsScaling ? Math.ceil(podCount * 1.5) : podCount)
};`,
          outputVariables: [
            { name: "cpu_usage_pct", type: "string" },
            { name: "memory_usage_pct", type: "string" },
            { name: "request_rate", type: "string" },
            { name: "error_rate", type: "string" },
            { name: "current_pod_count", type: "string" },
            { name: "needs_scaling", type: "string" },
            { name: "is_critical", type: "string" },
            { name: "scaling_reason", type: "string" },
            { name: "recommended_pods", type: "string" },
          ],
        },
      }),

      // 4. Scaling event condition
      n("4", "condition", "Critical scaling event?", 760, {
        conditionData: [
          {
            variable: "is_critical",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. SRE PagerDuty escalation (True branch)
      pagerDutyNode(
        "5",
        "PagerDuty: SRE scaling escalation",
        1000,
        "CRITICAL SCALING: {scaling_reason}",
        "critical",
        "app-scaling-monitor",
        '"cpu":"{cpu_usage_pct}%","memory":"{memory_usage_pct}%","requests":"{request_rate}/s","pods":"{current_pod_count}","recommended":"{recommended_pods}"',
        -220,
      ),

      // 6. Scaling notification (Slack)
      slackNode(
        "6",
        "Notify platform team (Slack)",
        1240,
        ":chart_with_upwards_trend: *Application Scaling Alert*\\nSeverity: {is_critical}\\nCPU: {cpu_usage_pct}% | Memory: {memory_usage_pct}%\\nRequest Rate: {request_rate}/s | Errors: {error_rate}/s\\nCurrent Pods: {current_pod_count} | Recommended: {recommended_pods}\\nReason: {scaling_reason}",
      ),

      // 7. Capacity planning dashboard (Sheets)
      sheetsNode(
        "7",
        "Update capacity planning dashboard (Sheets)",
        1480,
        "Capacity_Planning",
        '[["{cpu_usage_pct}","{memory_usage_pct}","{request_rate}","{error_rate}","{current_pod_count}","{recommended_pods}","{needs_scaling}","{is_critical}","{scaling_reason}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical -> PagerDuty
      e("4", "6", "output_2"), // non-critical -> Slack only
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 813. AUDIT LOG ANOMALY DETECTOR (8 nodes)
  // ===================================================================

  "Audit Log Anomaly Detector": {
    summary:
      "Ingests audit logs, normalizes events, detects behavioral anomalies, and responds to security threats. An audit log webhook captures events, runCode normalizes heterogeneous log formats, compares against behavioral baselines, AI classifies anomaly type and intent, a severity condition routes critical threats to PagerDuty, automated containment disables compromised accounts, and all incidents are documented in Google Sheets.",
    nodes: [
      trigger("Webhook: Audit log event ingestion", "webhook_app", {
        source: "Identity Provider / Cloud Platform",
        event: "audit.event",
        howItStarts:
          "Audit log events are forwarded from identity providers (Okta, Auth0, Azure AD) or cloud platforms (AWS CloudTrail, GCP Audit) whenever a security-relevant action occurs",
        setup:
          "Configure audit log streaming from your identity provider or cloud platform to this webhook URL. In Okta: Settings -> Log Streaming. In AWS: CloudTrail -> EventBridge -> API destination.",
        inputFields: [],
      }),

      // 2. Event normalization (runCode)
      n("2", "runCode", "Normalize audit log event", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize heterogeneous audit log formats into a common schema
const raw = JSON.parse(vars.webhook_payload || "{}");

// Handle Okta, AWS CloudTrail, Azure AD, GCP formats
const actor = raw.actor?.displayName || raw.userIdentity?.userName || raw.initiatedBy?.user?.displayName || raw.protoPayload?.authenticationInfo?.principalEmail || "unknown";
const actorId = raw.actor?.id || raw.userIdentity?.arn || raw.initiatedBy?.user?.id || raw.protoPayload?.authenticationInfo?.principalEmail || "unknown";
const action = raw.eventType || raw.eventName || raw.operationName || raw.protoPayload?.methodName || "unknown";
const target = raw.target?.[0]?.displayName || raw.requestParameters?.instanceId || raw.targetResources?.[0]?.displayName || raw.resource?.labels?.instance_id || "unknown";
const sourceIp = raw.client?.ipAddress || raw.sourceIPAddress || raw.callerIpAddress || raw.protoPayload?.requestMetadata?.callerIp || "unknown";
const timestamp = raw.published || raw.eventTime || raw.activityDateTime || raw.timestamp || new Date().toISOString();
const outcome = raw.outcome?.result || raw.responseElements?.return || raw.result || "unknown";
const severity = raw.severity || raw.severityLevel || "INFO";

// Detect suspicious patterns
const isOffHours = new Date(timestamp).getHours() < 6 || new Date(timestamp).getHours() > 22;
const isFailedAuth = action.toLowerCase().includes("login") && outcome.toLowerCase().includes("fail");
const isPrivilegedAction = action.toLowerCase().includes("admin") || action.toLowerCase().includes("delete") || action.toLowerCase().includes("permission");
const riskScore = (isOffHours ? 30 : 0) + (isFailedAuth ? 40 : 0) + (isPrivilegedAction ? 25 : 0);

return {
  actor_name: actor,
  actor_id: actorId,
  action_type: action,
  target_resource: target,
  source_ip: sourceIp,
  event_timestamp: timestamp,
  outcome: outcome,
  original_severity: severity,
  is_off_hours: isOffHours ? "yes" : "no",
  is_failed_auth: isFailedAuth ? "yes" : "no",
  is_privileged: isPrivilegedAction ? "yes" : "no",
  initial_risk_score: String(riskScore)
};`,
          outputVariables: [
            { name: "actor_name", type: "string" },
            { name: "actor_id", type: "string" },
            { name: "action_type", type: "string" },
            { name: "target_resource", type: "string" },
            { name: "source_ip", type: "string" },
            { name: "event_timestamp", type: "string" },
            { name: "outcome", type: "string" },
            { name: "original_severity", type: "string" },
            { name: "is_off_hours", type: "string" },
            { name: "is_failed_auth", type: "string" },
            { name: "is_privileged", type: "string" },
            { name: "initial_risk_score", type: "string" },
          ],
        },
      }),

      // 3. Behavioral baseline comparison
      aiNode(
        "3",
        "AI: Compare against behavioral baseline",
        520,
        "You are a security operations analyst. Given a normalized audit log event and its risk indicators, compare against typical behavioral baselines: normal login hours, expected IP ranges, typical action patterns, and role-based access norms. Determine whether this event represents an anomaly and classify it. Respond JSON: {is_anomalous: string, anomaly_classification: string, threat_category: string, confidence: number, behavioral_deviation: string, similar_ttps: string}",
        "Actor: {actor_name} ({actor_id}). Action: {action_type}. Target: {target_resource}. Source IP: {source_ip}. Outcome: {outcome}. Off-hours: {is_off_hours}. Failed auth: {is_failed_auth}. Privileged: {is_privileged}. Initial risk: {initial_risk_score}.",
        [
          { field: "is_anomalous", as: "is_anomalous", type: "string" },
          {
            field: "anomaly_classification",
            as: "anomaly_class",
            type: "string",
          },
          { field: "threat_category", as: "threat_category", type: "string" },
          { field: "confidence", as: "anomaly_confidence", type: "number" },
          {
            field: "behavioral_deviation",
            as: "behavioral_deviation",
            type: "string",
          },
          { field: "similar_ttps", as: "similar_ttps", type: "string" },
        ],
      ),

      // 4. Anomaly classification (AI) — additional context enrichment
      aiNode(
        "4",
        "AI: Classify severity and response",
        760,
        "You are a security incident responder. Given an anomalous audit event with classification, determine the appropriate severity level and response actions. Consider: account compromise indicators, data exfiltration patterns, privilege escalation attempts, and lateral movement signatures. Respond JSON: {final_severity: string, requires_containment: string, containment_action: string, investigation_steps: string, ioc_indicators: string, recommended_notification: string}",
        "Anomaly: {anomaly_class} ({threat_category}). Confidence: {anomaly_confidence}%. Deviation: {behavioral_deviation}. TTPs: {similar_ttps}. Actor: {actor_name}. Action: {action_type} on {target_resource}. IP: {source_ip}. Risk score: {initial_risk_score}.",
        [
          { field: "final_severity", as: "final_severity", type: "string" },
          {
            field: "requires_containment",
            as: "requires_containment",
            type: "string",
          },
          {
            field: "containment_action",
            as: "containment_action",
            type: "string",
          },
          {
            field: "investigation_steps",
            as: "investigation_steps",
            type: "string",
          },
          { field: "ioc_indicators", as: "ioc_indicators", type: "string" },
          {
            field: "recommended_notification",
            as: "recommended_notification",
            type: "string",
          },
        ],
      ),

      // 5. Severity condition
      n("5", "condition", "Severity requires immediate containment?", 1000, {
        conditionData: [
          {
            variable: "requires_containment",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Critical security PagerDuty alert (True branch)
      pagerDutyNode(
        "6",
        "PagerDuty: Security incident alert",
        1240,
        "SECURITY ANOMALY: {anomaly_class} by {actor_name} - {threat_category}",
        "critical",
        "audit-log-anomaly-detector",
        '"actor":"{actor_name}","action":"{action_type}","target":"{target_resource}","ip":"{source_ip}","severity":"{final_severity}","threat":"{threat_category}","containment":"{containment_action}"',
        -220,
      ),

      // 7. Automated containment response
      n("7", "hitApi", "Execute automated containment", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}.okta.com/api/v1/users/{actor_id}/lifecycle/suspend",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "status",
              customName: "containment_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Incident documentation (Sheets)
      sheetsNode(
        "8",
        "Document security incident (Sheets)",
        1720,
        "Security_Incidents",
        '[["{event_timestamp}","{actor_name}","{action_type}","{target_resource}","{source_ip}","{anomaly_class}","{threat_category}","{final_severity}","{containment_action}","{containment_status}","{ioc_indicators}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // requires containment -> PagerDuty
      e("5", "8", "output_2"), // no containment needed -> document only
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 814. AUTOMATED RUNBOOK EXECUTOR (9 nodes)
  // ===================================================================

  "Automated Runbook Executor": {
    summary:
      "Automatically selects and executes runbooks in response to incidents. An incident alert triggers runbook selection via AI, a condition handles missing runbooks, runCode performs pre-execution safety checks, the runbook steps execute via API, a step failure condition routes errors, post-execution health verification confirms resolution, Slack notifies the team, and runbook effectiveness is tracked in Google Sheets.",
    nodes: [
      trigger("Webhook: Incident alert", "webhook_app", {
        source: "PagerDuty / Datadog / OpsGenie",
        event: "incident.triggered",
        howItStarts:
          "A monitoring system fires an incident alert that triggers automated runbook selection and execution",
        setup:
          "Configure PagerDuty, Datadog, or OpsGenie to forward incident alerts to this workflow URL. Ensure runbook repository API is accessible. Set safety check thresholds for automated execution.",
        inputFields: [],
      }),

      // 2. Runbook selection (AI)
      aiNode(
        "2",
        "AI: Select appropriate runbook",
        280,
        "You are an SRE automation specialist. Given an incident alert payload, identify the incident type, affected service, severity, and select the most appropriate runbook from the standard library. Extract all relevant context. Respond JSON: {incident_id: string, incident_type: string, affected_service: string, severity: string, description: string, runbook_id: string, runbook_name: string, runbook_found: string, runbook_steps: string, estimated_duration_min: number, requires_approval: string}",
        "{webhook_payload}",
        [
          { field: "incident_id", as: "incident_id", type: "string" },
          { field: "incident_type", as: "incident_type", type: "string" },
          { field: "affected_service", as: "affected_service", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          { field: "description", as: "incident_description", type: "string" },
          { field: "runbook_id", as: "runbook_id", type: "string" },
          { field: "runbook_name", as: "runbook_name", type: "string" },
          { field: "runbook_found", as: "runbook_found", type: "string" },
          { field: "runbook_steps", as: "runbook_steps", type: "string" },
          {
            field: "estimated_duration_min",
            as: "estimated_duration",
            type: "number",
          },
          {
            field: "requires_approval",
            as: "requires_approval",
            type: "string",
          },
        ],
      ),

      // 3. No matching runbook condition
      n("3", "condition", "Matching runbook found?", 520, {
        conditionData: [
          {
            variable: "runbook_found",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Pre-execution safety checks (runCode) — True branch
      n("4", "runCode", "Pre-execution safety checks", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate it's safe to execute the selected runbook
const severity = vars.severity || "unknown";
const runbookName = vars.runbook_name || "";
const affectedService = vars.affected_service || "";
const requiresApproval = vars.requires_approval || "no";

let safetyChecks = [];
let allPassed = true;

// Check 1: Production blast radius
const isProduction = affectedService.toLowerCase().includes("prod") || affectedService.toLowerCase().includes("live");
if (isProduction && severity === "critical") {
  safetyChecks.push("WARN: Production critical incident - extra caution");
}

// Check 2: Time-of-day check (avoid non-business hours for non-critical)
const hour = new Date().getHours();
const isBusinessHours = hour >= 8 && hour <= 18;
if (!isBusinessHours && severity !== "critical") {
  safetyChecks.push("INFO: Off-hours execution for non-critical incident");
}

// Check 3: Runbook has defined steps
const steps = vars.runbook_steps || "";
if (steps.length < 20) {
  safetyChecks.push("FAIL: Runbook steps insufficient or empty");
  allPassed = false;
}

// Check 4: Requires approval gate
if (requiresApproval === "yes") {
  safetyChecks.push("HOLD: Runbook requires manual approval before execution");
  allPassed = false;
}

const safeToExecute = allPassed ? "yes" : "no";

return {
  safe_to_execute: safeToExecute,
  safety_check_results: safetyChecks.join("; ") || "All safety checks passed",
  is_production: isProduction ? "yes" : "no",
  is_business_hours: isBusinessHours ? "yes" : "no",
  execution_risk: isProduction && severity === "critical" ? "high" : "standard"
};`,
          outputVariables: [
            { name: "safe_to_execute", type: "string" },
            { name: "safety_check_results", type: "string" },
            { name: "is_production", type: "string" },
            { name: "is_business_hours", type: "string" },
            { name: "execution_risk", type: "string" },
          ],
        },
      }),

      // 5. Runbook step execution
      n("5", "hitApi", "Execute runbook steps", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{rundeck_url}/api/41/job/{runbook_id}/run",
          headers: [
            { key: "X-Rundeck-Auth-Token", value: "{rundeck_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "argString",
              value:
                "-service {affected_service} -severity {severity} -incident {incident_id}",
            },
          ],
          variables: [
            { name: "id", customName: "execution_id", type: "string" },
            { name: "status", customName: "execution_status", type: "string" },
            { name: "permalink", customName: "execution_url", type: "string" },
          ],
        },
      }),

      // 6. Step failure condition
      n("6", "condition", "Runbook execution succeeded?", 1240, {
        conditionData: [
          {
            variable: "execution_status",
            value: "succeeded",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Post-execution health verification (True branch)
      n("7", "hitApi", "Verify service health post-execution", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://{health_check_url}/api/v1/services/{affected_service}/health",
          headers: [
            { key: "Authorization", value: "Bearer {monitoring_token}" },
          ],
          body: [],
          variables: [
            { name: "status", customName: "health_status", type: "string" },
            {
              name: "checks",
              customName: "health_checks_json",
              type: "string",
            },
          ],
        },
      }),

      // 8. Resolution notification (Slack)
      slackNode(
        "8",
        "Notify team: runbook execution result (Slack)",
        1720,
        ":robot_face: *Automated Runbook Execution*\\nIncident: {incident_id} ({incident_type})\\nService: {affected_service} | Severity: {severity}\\nRunbook: {runbook_name}\\nExecution: {execution_status}\\nHealth: {health_status}\\nSafety Checks: {safety_check_results}\\nExecution URL: {execution_url}",
      ),

      // 9. Runbook effectiveness tracking (Sheets)
      sheetsNode(
        "9",
        "Track runbook effectiveness (Sheets)",
        1960,
        "Runbook_Effectiveness",
        '[["{incident_id}","{incident_type}","{affected_service}","{severity}","{runbook_name}","{execution_status}","{health_status}","{estimated_duration}","{safety_check_results}","{execution_risk}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // runbook found -> safety checks
      e("3", "8", "output_2"), // no runbook -> notify team to handle manually
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // success -> health verification
      e("6", "8", "output_2"), // failure -> notify team of failed execution
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 815. BLOG CONTENT IDEA GENERATOR (7 nodes)
  // ===================================================================

  "Blog Content Idea Generator": {
    summary:
      "Generates data-driven blog content ideas using trend research and competitive analysis. A content planning schedule triggers SerpAPI trend and keyword research, runCode audits existing content to avoid duplication, AI generates fresh content ideas with SEO hooks, a calendar conflict check ensures no scheduling overlaps, ideas are delivered to the content team via Slack, and selected ideas are written to the editorial calendar in Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly content ideation", "schedule", {
        schedule: "0 9 * * 2",
        howItStarts:
          "Runs every Tuesday at 9 AM to research trending topics, analyze competitive gaps, and generate fresh content ideas for the editorial calendar",
        setup:
          "Configure SerpAPI key for trend/keyword research. Connect Google Sheets for editorial calendar. Set up Slack webhook for content team notifications.",
        inputFields: [],
      }),

      // 2. SerpAPI trend/keyword research
      n("2", "hitApi", "Research trending topics (SerpAPI)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search?engine=google_trends&q={content_niche}&data_type=TIMESERIES&date=today+1-m",
          headers: [],
          body: [],
          variables: [
            {
              name: "interest_over_time",
              customName: "trend_data",
              type: "string",
            },
            {
              name: "related_queries",
              customName: "related_queries",
              type: "string",
            },
          ],
        },
      }),

      // 3. Existing content audit (runCode)
      n("3", "runCode", "Audit existing content for gaps", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze trend data and identify content gaps
const trendData = vars.trend_data || "[]";
const relatedQueries = vars.related_queries || "[]";

// Parse trending topics
let topics = [];
try {
  const trends = JSON.parse(trendData);
  const queries = JSON.parse(relatedQueries);

  // Extract rising topics
  if (Array.isArray(trends)) {
    trends.forEach(t => {
      if (t.query || t.value) topics.push(t.query || String(t.value));
    });
  }
  // Add related queries
  const rising = queries.rising || queries.top || [];
  (Array.isArray(rising) ? rising : []).forEach(q => {
    topics.push(q.query || q.term || "");
  });
} catch(e) {
  topics = ["industry trends", "best practices", "how-to guides", "case studies", "expert roundup"];
}

// Remove duplicates and empty
topics = [...new Set(topics.filter(t => t.length > 0))].slice(0, 15);
const topicCount = topics.length;
const topicList = topics.join(", ");

// Content format suggestions
const formats = ["How-to guide", "Listicle", "Case study", "Expert roundup", "Data analysis", "Comparison", "Tutorial", "Industry report"];
const suggestedFormats = formats.slice(0, 4).join(", ");

return {
  trending_topics: topicList,
  topic_count: String(topicCount),
  suggested_formats: suggestedFormats,
  content_gap_analysis: "Analyzed " + topicCount + " trending topics with rising search interest"
};`,
          outputVariables: [
            { name: "trending_topics", type: "string" },
            { name: "topic_count", type: "string" },
            { name: "suggested_formats", type: "string" },
            { name: "content_gap_analysis", type: "string" },
          ],
        },
      }),

      // 4. AI content ideation
      aiNode(
        "4",
        "AI: Generate content ideas with SEO hooks",
        760,
        "You are a content strategist and SEO specialist. Given trending topics and content gap analysis, generate 5 compelling blog post ideas with SEO-optimized titles, target keywords, estimated search volume tier, content format, estimated word count, and a brief pitch. Ensure ideas are differentiated and cover various funnel stages. Respond JSON: {ideas: string, top_idea_title: string, top_idea_keyword: string, top_idea_format: string, total_ideas: number, has_calendar_conflict: string, target_publish_dates: string}",
        "Trending topics: {trending_topics}. Gap analysis: {content_gap_analysis}. Suggested formats: {suggested_formats}. Topic count: {topic_count}.",
        [
          { field: "ideas", as: "content_ideas", type: "string" },
          { field: "top_idea_title", as: "top_idea_title", type: "string" },
          { field: "top_idea_keyword", as: "top_idea_keyword", type: "string" },
          { field: "top_idea_format", as: "top_idea_format", type: "string" },
          { field: "total_ideas", as: "total_ideas", type: "number" },
          {
            field: "has_calendar_conflict",
            as: "has_calendar_conflict",
            type: "string",
          },
          {
            field: "target_publish_dates",
            as: "target_publish_dates",
            type: "string",
          },
        ],
      ),

      // 5. Calendar conflict check condition
      n("5", "condition", "Calendar has scheduling conflicts?", 1000, {
        conditionData: [
          {
            variable: "has_calendar_conflict",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Idea package delivery (Slack) — both branches
      slackNode(
        "6",
        "Deliver content ideas to team (Slack)",
        1240,
        ":bulb: *Weekly Content Ideas*\\n{total_ideas} ideas generated from {topic_count} trending topics\\n\\n:star: *Top Pick:* {top_idea_title}\\nKeyword: {top_idea_keyword} | Format: {top_idea_format}\\n\\n*All Ideas:*\\n{content_ideas}\\n\\nTarget dates: {target_publish_dates}\\nCalendar conflicts: {has_calendar_conflict}",
      ),

      // 7. Selected ideas to editorial calendar (Sheets)
      sheetsNode(
        "7",
        "Add ideas to editorial calendar (Sheets)",
        1480,
        "Editorial_Calendar",
        '[["{top_idea_title}","{top_idea_keyword}","{top_idea_format}","{target_publish_dates}","ideation","{content_ideas}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // conflicts -> Slack (with conflict warning)
      e("5", "6", "output_2"), // no conflicts -> Slack (clean delivery)
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 816. BLOG POST TO SOCIAL MEDIA REPURPOSER (8 nodes)
  // ===================================================================

  "Blog Post to Social Media Repurposer": {
    summary:
      "Automatically repurposes blog posts into multi-platform social media content. A blog publish webhook triggers AI content extraction and analysis, generates platform-specific posts for Twitter, LinkedIn, and Instagram, creates image assets via DALL-E, an approval gate lets the content team review before publishing, approved content is scheduled via Buffer, runCode verifies cross-links between platforms, and performance is tracked in Google Sheets.",
    nodes: [
      trigger("Webhook: Blog post published", "webhook_app", {
        source: "WordPress / Ghost / Contentful",
        event: "post.published",
        howItStarts:
          "A new blog post is published on the company blog, triggering automatic social media content generation across all platforms",
        setup:
          "Configure CMS webhook to fire on post publish events. Connect OpenAI for content generation and DALL-E for image creation. Set up Buffer for social scheduling. Connect Google Sheets for performance tracking.",
        inputFields: [],
      }),

      // 2. Content extraction + analysis (AI)
      aiNode(
        "2",
        "AI: Extract and analyze blog content",
        280,
        "You are a content repurposing specialist. Parse this blog publish webhook payload to extract the post title, URL, full content, key takeaways, target audience, and tone. Analyze the content to identify the most shareable angles for different social platforms. Respond JSON: {post_title: string, post_url: string, post_content: string, key_takeaways: string, target_audience: string, content_tone: string, shareable_angles: string, hashtag_suggestions: string, image_prompt: string}",
        "{webhook_payload}",
        [
          { field: "post_title", as: "post_title", type: "string" },
          { field: "post_url", as: "post_url", type: "string" },
          { field: "post_content", as: "post_content", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "content_tone", as: "content_tone", type: "string" },
          { field: "shareable_angles", as: "shareable_angles", type: "string" },
          { field: "hashtag_suggestions", as: "hashtags", type: "string" },
          { field: "image_prompt", as: "image_prompt", type: "string" },
        ],
      ),

      // 3. Multi-platform content generation
      aiNode(
        "3",
        "AI: Generate platform-specific social posts",
        520,
        "You are a social media manager. Given blog content and shareable angles, generate optimized posts for: 1) Twitter (280 chars, punchy hook + link), 2) LinkedIn (professional tone, insight-led, 2-3 paragraphs), 3) Instagram (visual story caption, emoji-rich, CTA). Each must feel native to the platform. Respond JSON: {twitter_post: string, linkedin_post: string, instagram_caption: string, posting_schedule: string, best_posting_times: string}",
        "Title: {post_title}. URL: {post_url}. Key takeaways: {key_takeaways}. Angles: {shareable_angles}. Tone: {content_tone}. Audience: {target_audience}. Hashtags: {hashtags}.",
        [
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          { field: "posting_schedule", as: "posting_schedule", type: "string" },
          {
            field: "best_posting_times",
            as: "best_posting_times",
            type: "string",
          },
        ],
      ),

      // 4. Image asset generation (DALL-E)
      n("4", "hitApi", "Generate social image via DALL-E", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "{image_prompt}. Professional social media graphic for '{post_title}'. Clean, modern design. No text in the image.",
            },
            { key: "n", value: "1" },
            { key: "size", value: "1024x1024" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "generated_image_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "revised_image_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 5. Content review approval gate
      n("5", "approvalGate", "Review social content before publishing", 1000, {
        approvalGateData: {
          message:
            "Social Media Content Review for: {post_title}\\n\\nTwitter:\\n{twitter_post}\\n\\nLinkedIn:\\n{linkedin_post}\\n\\nInstagram:\\n{instagram_caption}\\n\\nHashtags: {hashtags}\\nImage: {generated_image_url}\\nSchedule: {posting_schedule}\\n\\nApprove to schedule publishing via Buffer?",
        },
      }),

      // 6. Scheduled publishing (Buffer)
      n("6", "hitApi", "Schedule social posts (Buffer)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "text", value: "{twitter_post}" },
            { key: "profile_ids[]", value: "{buffer_profile_ids}" },
            { key: "media[link]", value: "{post_url}" },
            { key: "media[photo]", value: "{generated_image_url}" },
            { key: "scheduled_at", value: "{best_posting_times}" },
          ],
          variables: [
            { name: "success", customName: "buffer_success", type: "boolean" },
            {
              name: "updates[0].id",
              customName: "buffer_update_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Cross-link verification (runCode)
      n("7", "runCode", "Verify cross-platform links", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify that all social posts contain correct links and image references
const postUrl = vars.post_url || "";
const twitterPost = vars.twitter_post || "";
const linkedinPost = vars.linkedin_post || "";
const instagramCaption = vars.instagram_caption || "";
const imageUrl = vars.generated_image_url || "";

let issues = [];
if (!twitterPost.includes("http") && !postUrl) issues.push("Twitter post missing link");
if (!linkedinPost.includes("http") && !postUrl) issues.push("LinkedIn post missing link");
if (!imageUrl || imageUrl.length < 10) issues.push("Generated image URL missing or invalid");

const bufferSuccess = vars.buffer_success === "true" || vars.buffer_success === true;
if (!bufferSuccess) issues.push("Buffer scheduling may have failed");

const allLinksValid = issues.length === 0 ? "yes" : "no";
const platformCount = 3; // Twitter, LinkedIn, Instagram

return {
  links_valid: allLinksValid,
  link_issues: issues.join("; ") || "All links verified",
  platforms_posted: String(platformCount),
  buffer_scheduled: bufferSuccess ? "yes" : "no"
};`,
          outputVariables: [
            { name: "links_valid", type: "string" },
            { name: "link_issues", type: "string" },
            { name: "platforms_posted", type: "string" },
            { name: "buffer_scheduled", type: "string" },
          ],
        },
      }),

      // 8. Performance tracking (Sheets)
      sheetsNode(
        "8",
        "Track social repurposing (Sheets)",
        1720,
        "Social_Repurposing",
        '[["{post_title}","{post_url}","{platforms_posted}","{buffer_scheduled}","{links_valid}","{link_issues}","{posting_schedule}","{hashtags}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 817. BUG REPORT COLLECTOR (7 nodes)
  // ===================================================================

  "Bug Report Collector": {
    summary:
      "Collects, normalizes, and triages bug reports with AI-powered deduplication. A bug report webhook captures submissions, runCode normalizes report format and extracts structured data, AI checks for duplicate reports against recent bugs, a duplicate condition routes accordingly, AI assesses severity for new bugs, a Jira issue is created with full context, and the reporter receives an acknowledgment email.",
    nodes: [
      trigger("Webhook: Bug report submission", "webhook_form", {
        source: "Internal Portal / Support Widget / API",
        event: "bug.reported",
        howItStarts:
          "A user submits a bug report through an internal portal, support widget, in-app feedback form, or API endpoint",
        setup:
          "Configure your bug report form or support widget to POST submissions to this workflow URL. Include any available context (user agent, page URL, reproduction steps, screenshots).",
        inputFields: [],
      }),

      // 2. Report normalization (runCode)
      n("2", "runCode", "Normalize bug report data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize bug report from various submission formats
const raw = JSON.parse(vars.webhook_payload || "{}");

// Extract fields regardless of source format
const title = raw.title || raw.subject || raw.summary || raw.bug_title || "Untitled bug report";
const description = raw.description || raw.body || raw.details || raw.bug_description || raw.message || "";
const reporterEmail = raw.email || raw.reporter_email || raw.user_email || raw.submitted_by || "";
const reporterName = raw.name || raw.reporter_name || raw.user_name || reporterEmail.split("@")[0] || "Anonymous";
const steps = raw.steps_to_reproduce || raw.reproduction_steps || raw.steps || "";
const expected = raw.expected_behavior || raw.expected || "";
const actual = raw.actual_behavior || raw.actual || "";
const environment = raw.environment || raw.env || raw.platform || "";
const userAgent = raw.user_agent || raw.browser || "";
const pageUrl = raw.page_url || raw.url || raw.page || "";
const screenshotUrl = raw.screenshot || raw.screenshot_url || raw.attachment || "";

const reportText = title + ". " + description + ". Steps: " + steps + ". Expected: " + expected + ". Actual: " + actual;

return {
  bug_title: title,
  bug_description: description,
  reporter_email: reporterEmail,
  reporter_name: reporterName,
  steps_to_reproduce: steps,
  expected_behavior: expected,
  actual_behavior: actual,
  environment_info: environment,
  user_agent: userAgent,
  page_url: pageUrl,
  screenshot_url: screenshotUrl,
  full_report_text: reportText
};`,
          outputVariables: [
            { name: "bug_title", type: "string" },
            { name: "bug_description", type: "string" },
            { name: "reporter_email", type: "string" },
            { name: "reporter_name", type: "string" },
            { name: "steps_to_reproduce", type: "string" },
            { name: "expected_behavior", type: "string" },
            { name: "actual_behavior", type: "string" },
            { name: "environment_info", type: "string" },
            { name: "user_agent", type: "string" },
            { name: "page_url", type: "string" },
            { name: "screenshot_url", type: "string" },
            { name: "full_report_text", type: "string" },
          ],
        },
      }),

      // 3. AI deduplication
      aiNode(
        "3",
        "AI: Check for duplicate bug reports",
        520,
        "You are a QA engineer specializing in bug triage. Given this bug report, determine if it is likely a duplicate of a known issue based on the title, description, affected area, and steps. Look for semantic similarity with common bugs. Respond JSON: {is_duplicate: string, duplicate_confidence: number, likely_duplicate_id: string, duplicate_reason: string, unique_aspects: string}",
        "Bug: {bug_title}. Description: {bug_description}. Steps: {steps_to_reproduce}. Page: {page_url}. Environment: {environment_info}.",
        [
          { field: "is_duplicate", as: "is_duplicate", type: "string" },
          {
            field: "duplicate_confidence",
            as: "duplicate_confidence",
            type: "number",
          },
          { field: "likely_duplicate_id", as: "duplicate_id", type: "string" },
          { field: "duplicate_reason", as: "duplicate_reason", type: "string" },
          { field: "unique_aspects", as: "unique_aspects", type: "string" },
        ],
      ),

      // 4. Duplicate handling condition
      n("4", "condition", "Is this a duplicate bug report?", 760, {
        conditionData: [
          {
            variable: "is_duplicate",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI severity assessment (False branch — new bug)
      aiNode(
        "5",
        "AI: Assess bug severity",
        1000,
        "You are a QA lead. Given this unique bug report, assess its severity and priority. Consider user impact, frequency, workaround availability, affected functionality criticality, and data integrity risk. Respond JSON: {severity: string, priority: string, component: string, affected_users_estimate: string, has_workaround: string, severity_rationale: string, suggested_labels: string}",
        "Bug: {bug_title}. Description: {bug_description}. Steps: {steps_to_reproduce}. Expected: {expected_behavior}. Actual: {actual_behavior}. Environment: {environment_info}. Page: {page_url}. Unique aspects: {unique_aspects}.",
        [
          { field: "severity", as: "bug_severity", type: "string" },
          { field: "priority", as: "bug_priority", type: "string" },
          { field: "component", as: "bug_component", type: "string" },
          {
            field: "affected_users_estimate",
            as: "affected_users",
            type: "string",
          },
          { field: "has_workaround", as: "has_workaround", type: "string" },
          {
            field: "severity_rationale",
            as: "severity_rationale",
            type: "string",
          },
          { field: "suggested_labels", as: "suggested_labels", type: "string" },
        ],
        380,
      ),

      // 6. Jira issue creation
      jiraNode(
        "6",
        "Create Jira bug ticket",
        1240,
        "{bug_title}",
        "Reported by: {reporter_name} ({reporter_email}). Description: {bug_description}. Steps to reproduce: {steps_to_reproduce}. Expected: {expected_behavior}. Actual: {actual_behavior}. Environment: {environment_info}. Page: {page_url}. Severity rationale: {severity_rationale}. Affected users: {affected_users}.",
        "Bug",
        "{bug_priority}",
        '"bug","{bug_component}","{bug_severity}"',
        380,
      ),

      // 7. Reporter acknowledgment (email)
      emailNode(
        "7",
        "Acknowledge bug report to reporter",
        1480,
        "{reporter_email}",
        "Bug Report Received: {bug_title}",
        "<h2>Thank you for your bug report</h2><p>Hi {reporter_name},</p><p>We've received your bug report: <strong>{bug_title}</strong></p><p>It has been assigned ticket <strong>{jira_ticket_key}</strong> with {bug_priority} priority.</p><p><strong>Severity:</strong> {bug_severity}</p><p><strong>Component:</strong> {bug_component}</p><p>We'll keep you updated on progress. Thank you for helping us improve!</p>",
        380,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"), // duplicate -> acknowledge and link to existing
      e("4", "5", "output_2"), // not duplicate -> severity assessment
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 818. BUG REPORT TO JIRA CREATOR (7 nodes)
  // ===================================================================

  "Bug Report to Jira Creator": {
    summary:
      "Automatically parses bug reports with AI and creates well-structured Jira issues. A bug report webhook triggers AI parsing to extract structured fields, AI classifies priority and component, an existing Jira issue search checks for duplicates, a duplicate condition prevents redundant tickets, new bugs become fully populated Jira issues, and stakeholders are notified via Slack.",
    nodes: [
      trigger("Webhook: Bug report received", "webhook_app", {
        source: "Support Tool / Internal Form / API",
        event: "bug.submitted",
        howItStarts:
          "A bug report is received from a support tool, internal form, or API endpoint and needs to be converted into a structured Jira issue",
        setup:
          "Configure your support tool or form to POST bug reports to this workflow URL. Set up Jira API credentials with issue creation permissions. Connect Slack for stakeholder notifications.",
        inputFields: [],
      }),

      // 2. AI report parsing
      aiNode(
        "2",
        "AI: Parse bug report into structured fields",
        280,
        "You are a QA automation engineer. Parse this raw bug report payload to extract structured fields for Jira creation. Handle any format — email forwarding, form submissions, API posts, or chat messages. Find the bug title, description, reproduction steps, expected vs actual behavior, environment, reporter, and any attached evidence. Respond JSON: {bug_title: string, bug_description: string, reproduction_steps: string, expected_behavior: string, actual_behavior: string, environment: string, reporter_email: string, reporter_name: string, affected_area: string, frequency: string, attached_evidence: string}",
        "{webhook_payload}",
        [
          { field: "bug_title", as: "bug_title", type: "string" },
          { field: "bug_description", as: "bug_description", type: "string" },
          {
            field: "reproduction_steps",
            as: "reproduction_steps",
            type: "string",
          },
          {
            field: "expected_behavior",
            as: "expected_behavior",
            type: "string",
          },
          { field: "actual_behavior", as: "actual_behavior", type: "string" },
          { field: "environment", as: "environment", type: "string" },
          { field: "reporter_email", as: "reporter_email", type: "string" },
          { field: "reporter_name", as: "reporter_name", type: "string" },
          { field: "affected_area", as: "affected_area", type: "string" },
          { field: "frequency", as: "bug_frequency", type: "string" },
          { field: "attached_evidence", as: "evidence_urls", type: "string" },
        ],
      ),

      // 3. Priority/component classification
      aiNode(
        "3",
        "AI: Classify priority and component",
        520,
        "You are a senior QA manager. Given the parsed bug report, classify its priority (Highest, High, Medium, Low, Lowest) and map it to the correct Jira component. Consider user impact, data integrity risk, frequency of occurrence, and presence of a workaround. Respond JSON: {priority: string, component: string, issue_type: string, labels: string, story_points: number, sprint_recommendation: string}",
        "Bug: {bug_title}. Description: {bug_description}. Area: {affected_area}. Frequency: {bug_frequency}. Expected: {expected_behavior}. Actual: {actual_behavior}. Environment: {environment}.",
        [
          { field: "priority", as: "jira_priority", type: "string" },
          { field: "component", as: "jira_component", type: "string" },
          { field: "issue_type", as: "jira_issue_type", type: "string" },
          { field: "labels", as: "jira_labels", type: "string" },
          { field: "story_points", as: "story_points", type: "number" },
          {
            field: "sprint_recommendation",
            as: "sprint_recommendation",
            type: "string",
          },
        ],
      ),

      // 4. Existing Jira issue search
      n("4", "hitApi", "Search existing Jira issues for duplicates", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=project%3D{jira_project_key}%20AND%20summary~%22{bug_title}%22%20AND%20status!%3DDone&maxResults=5",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total", customName: "duplicate_count", type: "number" },
            {
              name: "issues[0].key",
              customName: "existing_issue_key",
              type: "string",
            },
            {
              name: "issues[0].fields.summary",
              customName: "existing_issue_summary",
              type: "string",
            },
          ],
        },
      }),

      // 5. Duplicate condition
      n("5", "condition", "Potential duplicate found in Jira?", 1000, {
        conditionData: [
          {
            variable: "duplicate_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Jira issue creation (False branch — no duplicate)
      jiraNode(
        "6",
        "Create Jira bug issue",
        1240,
        "{bug_title}",
        "Reporter: {reporter_name} ({reporter_email}). Description: {bug_description}. Steps: {reproduction_steps}. Expected: {expected_behavior}. Actual: {actual_behavior}. Environment: {environment}. Frequency: {bug_frequency}. Evidence: {evidence_urls}. Sprint: {sprint_recommendation}.",
        "{jira_issue_type}",
        "{jira_priority}",
        '"bug","{jira_component}","{jira_labels}"',
        380,
      ),

      // 7. Stakeholder notification (Slack)
      slackNode(
        "7",
        "Notify stakeholders (Slack)",
        1480,
        ":bug: *New Bug Report -> Jira*\\nTitle: {bug_title}\\nTicket: {jira_ticket_key}\\nPriority: {jira_priority} | Component: {jira_component}\\nReporter: {reporter_name}\\nArea: {affected_area} | Frequency: {bug_frequency}\\nSprint: {sprint_recommendation}\\nDuplicate check: {duplicate_count} similar issues found",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // duplicate found -> notify with existing issue ref
      e("5", "6", "output_2"), // no duplicate -> create new Jira issue
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 819. BUG TRIAGE WORKFLOW (8 nodes)
  // ===================================================================

  "Bug Triage Workflow": {
    summary:
      "Comprehensive bug triage process with AI-powered assessment and sprint planning. A new bug ticket webhook triggers context enrichment from related systems, AI performs triage assessment considering severity, user impact, and effort, a severity condition routes critical bugs to a war room Slack channel, Jira sprint capacity is checked for scheduling, triage decisions are documented, and a triage summary digest is emailed to stakeholders.",
    nodes: [
      trigger("Webhook: New bug ticket created", "webhook_app", {
        source: "Jira / Linear / GitHub Issues",
        event: "issue.created",
        howItStarts:
          "A new bug ticket is created in the issue tracker, triggering the triage workflow for assessment, prioritization, and sprint assignment",
        setup:
          "Configure Jira, Linear, or GitHub webhook to fire on issue creation for bug-type tickets. Connect Slack for war room alerts. Set up email for triage digest distribution.",
        inputFields: [],
      }),

      // 2. Bug context enrichment
      aiNode(
        "2",
        "AI: Extract and enrich bug context",
        280,
        "You are a QA analyst. Parse the bug ticket webhook payload to extract all relevant context. Look for the issue key, title, description, reporter, assignee, component, and any linked issues regardless of the source system format (Jira, Linear, GitHub). Respond JSON: {issue_key: string, bug_title: string, bug_description: string, reporter: string, assignee: string, component: string, linked_issues: string, current_priority: string, labels: string, created_date: string}",
        "{webhook_payload}",
        [
          { field: "issue_key", as: "issue_key", type: "string" },
          { field: "bug_title", as: "bug_title", type: "string" },
          { field: "bug_description", as: "bug_description", type: "string" },
          { field: "reporter", as: "reporter", type: "string" },
          { field: "assignee", as: "assignee", type: "string" },
          { field: "component", as: "component", type: "string" },
          { field: "linked_issues", as: "linked_issues", type: "string" },
          { field: "current_priority", as: "current_priority", type: "string" },
          { field: "labels", as: "labels", type: "string" },
          { field: "created_date", as: "created_date", type: "string" },
        ],
      ),

      // 3. AI triage assessment
      aiNode(
        "3",
        "AI: Perform triage assessment",
        520,
        "You are a senior engineering manager performing bug triage. Assess this bug for severity (P0-Critical, P1-High, P2-Medium, P3-Low), estimate engineering effort, determine user impact scope, evaluate business impact, and recommend sprint assignment. Consider regression risk and whether this blocks other work. Respond JSON: {triage_severity: string, effort_estimate_hours: number, user_impact_scope: string, business_impact: string, regression_risk: string, blocks_other_work: string, recommended_sprint: string, triage_notes: string, requires_war_room: string}",
        "Issue: {issue_key}. Title: {bug_title}. Description: {bug_description}. Component: {component}. Current priority: {current_priority}. Linked: {linked_issues}. Labels: {labels}.",
        [
          { field: "triage_severity", as: "triage_severity", type: "string" },
          {
            field: "effort_estimate_hours",
            as: "effort_hours",
            type: "number",
          },
          { field: "user_impact_scope", as: "user_impact", type: "string" },
          { field: "business_impact", as: "business_impact", type: "string" },
          { field: "regression_risk", as: "regression_risk", type: "string" },
          { field: "blocks_other_work", as: "blocks_work", type: "string" },
          {
            field: "recommended_sprint",
            as: "recommended_sprint",
            type: "string",
          },
          { field: "triage_notes", as: "triage_notes", type: "string" },
          {
            field: "requires_war_room",
            as: "requires_war_room",
            type: "string",
          },
        ],
      ),

      // 4. Severity routing condition
      n("4", "condition", "Critical bug requiring war room?", 760, {
        conditionData: [
          {
            variable: "requires_war_room",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Critical bug war room (Slack) — True branch
      slackNode(
        "5",
        "Open war room for critical bug (Slack)",
        1000,
        ":rotating_light: *CRITICAL BUG WAR ROOM*\\nIssue: {issue_key} - {bug_title}\\nSeverity: {triage_severity}\\nComponent: {component}\\nUser Impact: {user_impact}\\nBusiness Impact: {business_impact}\\nRegression Risk: {regression_risk}\\nBlocks: {blocks_work}\\nEstimated Effort: {effort_hours}h\\n\\n@engineering @qa Please join #war-room-{issue_key} immediately.",
        -220,
      ),

      // 6. Sprint capacity check (Jira)
      n("6", "hitApi", "Check sprint capacity (Jira)", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/agile/1.0/board/{jira_board_id}/sprint?state=active&maxResults=1",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values[0].id",
              customName: "active_sprint_id",
              type: "string",
            },
            {
              name: "values[0].name",
              customName: "active_sprint_name",
              type: "string",
            },
            {
              name: "values[0].endDate",
              customName: "sprint_end_date",
              type: "string",
            },
          ],
        },
      }),

      // 7. Triage decision documentation
      sheetsNode(
        "7",
        "Document triage decision (Sheets)",
        1480,
        "Bug_Triage_Log",
        '[["{issue_key}","{bug_title}","{component}","{triage_severity}","{effort_hours}","{user_impact}","{business_impact}","{recommended_sprint}","{active_sprint_name}","{triage_notes}","{reporter}","=NOW()"]]',
      ),

      // 8. Triage summary digest (email)
      emailNode(
        "8",
        "Send triage digest (email)",
        1720,
        "engineering-leads@company.com",
        "Bug Triage: {issue_key} - {triage_severity} - {bug_title}",
        "<h2>Bug Triage Summary</h2><p><strong>Issue:</strong> {issue_key} - {bug_title}</p><p><strong>Severity:</strong> {triage_severity} | Effort: {effort_hours}h</p><p><strong>Component:</strong> {component}</p><p><strong>User Impact:</strong> {user_impact}</p><p><strong>Business Impact:</strong> {business_impact}</p><p><strong>Regression Risk:</strong> {regression_risk}</p><p><strong>Blocks Other Work:</strong> {blocks_work}</p><p><strong>Recommended Sprint:</strong> {recommended_sprint}</p><p><strong>Active Sprint:</strong> {active_sprint_name} (ends {sprint_end_date})</p><p><strong>Triage Notes:</strong> {triage_notes}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical -> war room
      e("4", "6", "output_2"), // non-critical -> sprint check directly
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 820. CHAOS ENGINEERING SCHEDULER (8 nodes)
  // ===================================================================

  "Chaos Engineering Scheduler": {
    summary:
      "Schedules and manages chaos engineering experiments with safety guardrails. A chaos experiment webhook triggers pre-experiment health baseline collection, runCode validates experiment safety parameters, a safety check condition gates execution, chaos injection runs via API, real-time impact monitoring tracks blast radius, an automatic abort condition with rollback protects against excessive damage, and experiment results are recorded in Google Sheets.",
    nodes: [
      trigger("Webhook: Chaos experiment trigger", "webhook_app", {
        source: "Chaos Platform / CI/CD / Manual",
        event: "chaos.experiment.requested",
        howItStarts:
          "A chaos engineering experiment is requested via the chaos platform, CI/CD pipeline integration, or manual trigger with experiment configuration",
        setup:
          "Configure chaos platform (Gremlin, Litmus, Chaos Monkey) webhook to POST experiment requests. Set up health check endpoints for baseline and monitoring. Connect Google Sheets for results tracking.",
        inputFields: [],
      }),

      // 2. Pre-experiment health baseline
      aiNode(
        "2",
        "AI: Parse experiment and capture health baseline",
        280,
        "You are a chaos engineering specialist. Parse the experiment request payload to identify the experiment type, target service, blast radius, duration, and safety parameters. Also establish a health baseline for the target service. Respond JSON: {experiment_id: string, experiment_type: string, target_service: string, blast_radius: string, duration_minutes: number, safety_threshold: number, hypothesis: string, baseline_health: string, baseline_latency_ms: number, baseline_error_rate: number, requester: string}",
        "{webhook_payload}",
        [
          { field: "experiment_id", as: "experiment_id", type: "string" },
          { field: "experiment_type", as: "experiment_type", type: "string" },
          { field: "target_service", as: "target_service", type: "string" },
          { field: "blast_radius", as: "blast_radius", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "safety_threshold", as: "safety_threshold", type: "number" },
          { field: "hypothesis", as: "hypothesis", type: "string" },
          { field: "baseline_health", as: "baseline_health", type: "string" },
          {
            field: "baseline_latency_ms",
            as: "baseline_latency",
            type: "number",
          },
          {
            field: "baseline_error_rate",
            as: "baseline_error_rate",
            type: "number",
          },
          { field: "requester", as: "requester", type: "string" },
        ],
      ),

      // 3. Experiment safety validation (runCode)
      n("3", "runCode", "Validate experiment safety parameters", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate chaos experiment safety before execution
const targetService = vars.target_service || "";
const blastRadius = vars.blast_radius || "unknown";
const durationMin = parseInt(vars.duration_minutes) || 0;
const safetyThreshold = parseInt(vars.safety_threshold) || 50;
const baselineHealth = vars.baseline_health || "";
const baselineLatency = parseInt(vars.baseline_latency) || 0;
const baselineErrorRate = parseFloat(vars.baseline_error_rate) || 0;
const experimentType = vars.experiment_type || "";

let safetyChecks = [];
let safeToRun = true;

// Check 1: Baseline health must be healthy
if (!baselineHealth.toLowerCase().includes("healthy") && !baselineHealth.toLowerCase().includes("ok") && !baselineHealth.toLowerCase().includes("pass")) {
  safetyChecks.push("FAIL: Target service not healthy at baseline (" + baselineHealth + ")");
  safeToRun = false;
}

// Check 2: Duration limits
if (durationMin > 30) {
  safetyChecks.push("FAIL: Duration " + durationMin + "min exceeds 30min safety limit");
  safeToRun = false;
} else if (durationMin > 15) {
  safetyChecks.push("WARN: Extended duration " + durationMin + "min - monitor closely");
}

// Check 3: Blast radius limits
const highBlast = blastRadius.toLowerCase().includes("all") || blastRadius.toLowerCase().includes("100%") || blastRadius.toLowerCase().includes("full");
if (highBlast) {
  safetyChecks.push("FAIL: 100% blast radius not permitted in automated runs");
  safeToRun = false;
}

// Check 4: Existing error rate must be low
if (baselineErrorRate > 5) {
  safetyChecks.push("FAIL: Baseline error rate " + baselineErrorRate + "% too high (max 5%)");
  safeToRun = false;
}

// Check 5: Business hours check
const hour = new Date().getHours();
const isBusinessHours = hour >= 9 && hour <= 17;
if (isBusinessHours && experimentType.toLowerCase().includes("network")) {
  safetyChecks.push("WARN: Network chaos during business hours");
}

return {
  safe_to_run: safeToRun ? "yes" : "no",
  safety_results: safetyChecks.join("; ") || "All safety checks passed",
  safety_check_count: String(safetyChecks.length),
  abort_threshold_latency: String(Math.round(baselineLatency * 3)),
  abort_threshold_errors: String(Math.min(baselineErrorRate + 10, safetyThreshold))
};`,
          outputVariables: [
            { name: "safe_to_run", type: "string" },
            { name: "safety_results", type: "string" },
            { name: "safety_check_count", type: "string" },
            { name: "abort_threshold_latency", type: "string" },
            { name: "abort_threshold_errors", type: "string" },
          ],
        },
      }),

      // 4. Safety check condition
      n("4", "condition", "Safe to run chaos experiment?", 760, {
        conditionData: [
          {
            variable: "safe_to_run",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Chaos injection execution (True branch)
      n("5", "hitApi", "Execute chaos injection", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.gremlin.com/v1/attacks/new",
          headers: [
            { key: "Authorization", value: "Key {gremlin_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "target",
              value: '{"type":"Exact","exact":["{target_service}"]}',
            },
            {
              key: "command",
              value:
                '{"type":"{experiment_type}","args":["-l","{duration_minutes}"]}',
            },
          ],
          variables: [
            { name: "attackId", customName: "attack_id", type: "string" },
            { name: "stage", customName: "attack_stage", type: "string" },
          ],
        },
      }),

      // 6. Real-time impact monitoring
      n("6", "hitApi", "Monitor experiment impact", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://{health_check_url}/api/v1/services/{target_service}/health",
          headers: [
            { key: "Authorization", value: "Bearer {monitoring_token}" },
          ],
          body: [],
          variables: [
            {
              name: "latency_ms",
              customName: "current_latency",
              type: "number",
            },
            {
              name: "error_rate",
              customName: "current_error_rate",
              type: "number",
            },
            {
              name: "status",
              customName: "current_health_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Automatic abort + rollback condition
      n("7", "condition", "Impact exceeds safety thresholds?", 1480, {
        conditionData: [
          {
            variable: "current_error_rate",
            value: "{abort_threshold_errors}",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
        ],
      }),

      // 8. Experiment results report (Sheets) — both paths converge
      sheetsNode(
        "8",
        "Record experiment results (Sheets)",
        1720,
        "Chaos_Experiments",
        '[["{experiment_id}","{experiment_type}","{target_service}","{blast_radius}","{duration_minutes}","{baseline_latency}","{current_latency}","{baseline_error_rate}","{current_error_rate}","{current_health_status}","{safety_results}","{hypothesis}","{requester}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // safe -> execute chaos
      e("4", "8", "output_2"), // unsafe -> log and skip
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // thresholds exceeded -> abort and log
      e("7", "8", "output_2"), // within bounds -> log success
    ],
  },

  // ===================================================================
  // 821. CODE COVERAGE REPORT GENERATOR (7 nodes)
  // ===================================================================

  "Code Coverage Report Generator": {
    summary:
      "Generates and evaluates code coverage reports from CI pipeline runs. A CI pipeline webhook triggers coverage data extraction via runCode, performs historical comparison with previous runs, evaluates coverage policy compliance via condition, posts PR comments or merge blocks accordingly, AI generates actionable coverage insights, and all data feeds into a Google Sheets dashboard.",
    nodes: [
      trigger("Webhook: CI pipeline completed", "webhook_app", {
        source: "GitHub Actions / CircleCI / Jenkins",
        event: "pipeline.completed",
        howItStarts:
          "A CI pipeline completes a test run with coverage reporting, triggering extraction and analysis of coverage metrics",
        setup:
          "Configure your CI pipeline to POST coverage results to this workflow URL after test completion. Include coverage summary JSON, PR number, branch name, and commit SHA.",
        inputFields: [],
      }),

      // 2. Coverage data extraction (runCode)
      n("2", "runCode", "Extract and normalize coverage data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and normalize coverage data from various CI formats
const raw = JSON.parse(vars.webhook_payload || "{}");

// Handle GitHub Actions, CircleCI, Jenkins, and generic formats
const prNumber = raw.pull_request?.number || raw.pr_number || raw.CHANGE_ID || "";
const branch = raw.ref?.replace("refs/heads/", "") || raw.branch || raw.BRANCH_NAME || "unknown";
const commitSha = raw.head_commit?.id || raw.sha || raw.commit || raw.GIT_COMMIT || "";
const repoName = raw.repository?.full_name || raw.repo || raw.JOB_NAME || "";

// Extract coverage metrics
const coverage = raw.coverage || raw.totals || raw.summary || {};
const lineCoverage = coverage.lines?.pct || coverage.line_rate || coverage.lineCoverage || coverage.lines || 0;
const branchCoverage = coverage.branches?.pct || coverage.branch_rate || coverage.branchCoverage || coverage.branches || 0;
const functionCoverage = coverage.functions?.pct || coverage.function_rate || coverage.functionCoverage || coverage.functions || 0;
const statementCoverage = coverage.statements?.pct || coverage.statement_rate || coverage.statementCoverage || coverage.statements || 0;

const overallCoverage = Math.round(((parseFloat(lineCoverage) + parseFloat(branchCoverage) + parseFloat(functionCoverage) + parseFloat(statementCoverage)) / 4) * 100) / 100;

// Coverage policy thresholds
const minCoverage = 80;
const coverageMet = overallCoverage >= minCoverage ? "yes" : "no";
const coverageDelta = raw.previous_coverage ? (overallCoverage - parseFloat(raw.previous_coverage)).toFixed(2) : "N/A";
const isRegression = coverageDelta !== "N/A" && parseFloat(coverageDelta) < -2;

return {
  pr_number: String(prNumber),
  branch_name: branch,
  commit_sha: commitSha,
  repo_name: repoName,
  line_coverage: String(lineCoverage),
  branch_coverage: String(branchCoverage),
  function_coverage: String(functionCoverage),
  statement_coverage: String(statementCoverage),
  overall_coverage: String(overallCoverage),
  coverage_policy_met: coverageMet,
  coverage_delta: String(coverageDelta),
  is_regression: isRegression ? "yes" : "no",
  min_coverage_threshold: String(minCoverage)
};`,
          outputVariables: [
            { name: "pr_number", type: "string" },
            { name: "branch_name", type: "string" },
            { name: "commit_sha", type: "string" },
            { name: "repo_name", type: "string" },
            { name: "line_coverage", type: "string" },
            { name: "branch_coverage", type: "string" },
            { name: "function_coverage", type: "string" },
            { name: "statement_coverage", type: "string" },
            { name: "overall_coverage", type: "string" },
            { name: "coverage_policy_met", type: "string" },
            { name: "coverage_delta", type: "string" },
            { name: "is_regression", type: "string" },
            { name: "min_coverage_threshold", type: "string" },
          ],
        },
      }),

      // 3. Historical comparison
      aiNode(
        "3",
        "AI: Historical coverage trend analysis",
        520,
        "You are a quality engineering analyst. Given the current coverage metrics and delta, analyze the coverage trend, identify areas of concern, and determine if this represents an improvement or regression. Consider line, branch, function, and statement coverage independently. Respond JSON: {trend_direction: string, trend_analysis: string, worst_metric: string, improvement_areas: string, risk_areas: string, comparison_summary: string}",
        "Repo: {repo_name}. Branch: {branch_name}. PR: #{pr_number}. Overall: {overall_coverage}%. Lines: {line_coverage}%. Branches: {branch_coverage}%. Functions: {function_coverage}%. Statements: {statement_coverage}%. Delta: {coverage_delta}%. Regression: {is_regression}. Min threshold: {min_coverage_threshold}%.",
        [
          { field: "trend_direction", as: "trend_direction", type: "string" },
          { field: "trend_analysis", as: "trend_analysis", type: "string" },
          { field: "worst_metric", as: "worst_metric", type: "string" },
          {
            field: "improvement_areas",
            as: "improvement_areas",
            type: "string",
          },
          { field: "risk_areas", as: "risk_areas", type: "string" },
          {
            field: "comparison_summary",
            as: "comparison_summary",
            type: "string",
          },
        ],
      ),

      // 4. Coverage policy evaluation condition
      n("4", "condition", "Coverage policy met?", 760, {
        conditionData: [
          {
            variable: "coverage_policy_met",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. PR comment/merge block — True = pass comment, False = block merge
      n("5", "hitApi", "Post PR coverage comment (GitHub)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.github.com/repos/{repo_name}/issues/{pr_number}/comments",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github+json" },
          ],
          body: [
            {
              key: "body",
              value:
                "## Coverage Report\\n\\n| Metric | Coverage |\\n|--------|---------|\\n| Lines | {line_coverage}% |\\n| Branches | {branch_coverage}% |\\n| Functions | {function_coverage}% |\\n| Statements | {statement_coverage}% |\\n| **Overall** | **{overall_coverage}%** |\\n\\n**Policy ({min_coverage_threshold}% min):** {coverage_policy_met}\\n**Delta:** {coverage_delta}%\\n**Trend:** {trend_direction}\\n\\n{comparison_summary}\\n\\n**Risk Areas:** {risk_areas}\\n**Improvement:** {improvement_areas}",
            },
          ],
          variables: [{ name: "id", customName: "comment_id", type: "string" }],
        },
      }),

      // 6. AI coverage insights
      aiNode(
        "6",
        "AI: Generate actionable coverage insights",
        1240,
        "You are a test engineering advisor. Given coverage metrics and trend analysis, generate specific actionable recommendations to improve coverage. Identify untested code patterns, suggest test strategies, and prioritize by risk. Respond JSON: {recommendations: string, priority_tests: string, estimated_effort_hours: number, coverage_target: number, test_strategy: string}",
        "Overall: {overall_coverage}% (delta: {coverage_delta}%). Worst metric: {worst_metric}. Risk areas: {risk_areas}. Trend: {trend_direction}. Analysis: {trend_analysis}.",
        [
          {
            field: "recommendations",
            as: "coverage_recommendations",
            type: "string",
          },
          { field: "priority_tests", as: "priority_tests", type: "string" },
          {
            field: "estimated_effort_hours",
            as: "effort_to_improve",
            type: "number",
          },
          {
            field: "coverage_target",
            as: "next_coverage_target",
            type: "number",
          },
          { field: "test_strategy", as: "test_strategy", type: "string" },
        ],
      ),

      // 7. Coverage dashboard (Sheets)
      sheetsNode(
        "7",
        "Update coverage dashboard (Sheets)",
        1480,
        "Coverage_Dashboard",
        '[["{repo_name}","{branch_name}","{pr_number}","{overall_coverage}","{line_coverage}","{branch_coverage}","{function_coverage}","{statement_coverage}","{coverage_delta}","{coverage_policy_met}","{trend_direction}","{worst_metric}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // policy met -> pass comment
      e("4", "5", "output_2"), // policy not met -> block/warning comment
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 822. CODE DEPLOYMENT APPROVAL WORKFLOW (9 nodes)
  // ===================================================================

  "Code Deployment Approval Workflow": {
    summary:
      "Risk-based deployment approval workflow with automated checks and staged rollout. A deployment request webhook triggers artifact analysis via runCode, AI assesses deployment risk, a risk-based condition routes high-risk deployments through additional approval via Slack, a delay handles approval timeouts, deployment authorization executes via API, health verification confirms the rollout, and completion notifications close the loop.",
    nodes: [
      trigger("Webhook: Deployment request", "webhook_app", {
        source: "CI/CD Pipeline / Release Manager",
        event: "deployment.requested",
        howItStarts:
          "A deployment request is submitted from the CI/CD pipeline or release management tool, initiating the approval workflow",
        setup:
          "Configure CI/CD pipeline (GitHub Actions, GitLab CI, ArgoCD) to POST deployment requests to this workflow URL when a release candidate is ready. Include artifact details, target environment, and change list.",
        inputFields: [],
      }),

      // 2. Artifact analysis (runCode)
      n("2", "runCode", "Analyze deployment artifact", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze deployment request and extract risk indicators
const raw = JSON.parse(vars.webhook_payload || "{}");

// Extract deployment details
const artifact = raw.artifact || raw.image || raw.package || raw.build || {};
const artifactName = artifact.name || raw.service || raw.app_name || "unknown";
const version = artifact.version || artifact.tag || raw.version || raw.tag || "latest";
const targetEnv = raw.environment || raw.target || raw.env || "staging";
const requester = raw.requester || raw.triggered_by || raw.user || "unknown";
const commitCount = raw.commit_count || raw.changes || (raw.commits || []).length || 0;
const changeList = raw.changelog || raw.change_list || raw.commits?.map(c => c.message).join("; ") || "";
const testsPass = raw.tests_passed !== false && raw.test_status !== "failed";
const previousVersion = raw.previous_version || raw.rollback_version || "";

// Risk scoring
let riskScore = 0;
const isProduction = targetEnv.toLowerCase().includes("prod") || targetEnv.toLowerCase().includes("live");
if (isProduction) riskScore += 30;
if (commitCount > 10) riskScore += 20;
if (!testsPass) riskScore += 40;
if (changeList.toLowerCase().includes("database") || changeList.toLowerCase().includes("migration")) riskScore += 25;
if (changeList.toLowerCase().includes("breaking") || changeList.toLowerCase().includes("major")) riskScore += 15;

const riskLevel = riskScore >= 50 ? "high" : riskScore >= 25 ? "medium" : "low";

return {
  artifact_name: artifactName,
  artifact_version: version,
  target_environment: targetEnv,
  deploy_requester: requester,
  commit_count: String(commitCount),
  change_summary: changeList || "No changelog provided",
  tests_passing: testsPass ? "yes" : "no",
  previous_version: previousVersion,
  is_production: isProduction ? "yes" : "no",
  risk_score: String(riskScore),
  risk_level: riskLevel
};`,
          outputVariables: [
            { name: "artifact_name", type: "string" },
            { name: "artifact_version", type: "string" },
            { name: "target_environment", type: "string" },
            { name: "deploy_requester", type: "string" },
            { name: "commit_count", type: "string" },
            { name: "change_summary", type: "string" },
            { name: "tests_passing", type: "string" },
            { name: "previous_version", type: "string" },
            { name: "is_production", type: "string" },
            { name: "risk_score", type: "string" },
            { name: "risk_level", type: "string" },
          ],
        },
      }),

      // 3. Risk assessment (AI)
      aiNode(
        "3",
        "AI: Assess deployment risk",
        520,
        "You are a release engineering specialist. Given deployment artifact analysis, assess the overall deployment risk, recommend a deployment strategy (blue-green, canary, rolling, or direct), and determine if additional approval is needed. Respond JSON: {deployment_strategy: string, risk_summary: string, requires_additional_approval: string, approval_tier: string, rollback_plan: string, monitoring_duration_min: number, blast_radius: string}",
        "Artifact: {artifact_name} v{artifact_version}. Target: {target_environment}. Commits: {commit_count}. Tests: {tests_passing}. Changes: {change_summary}. Risk: {risk_level} ({risk_score}). Production: {is_production}. Previous: {previous_version}.",
        [
          {
            field: "deployment_strategy",
            as: "deploy_strategy",
            type: "string",
          },
          { field: "risk_summary", as: "risk_summary", type: "string" },
          {
            field: "requires_additional_approval",
            as: "needs_extra_approval",
            type: "string",
          },
          { field: "approval_tier", as: "approval_tier", type: "string" },
          { field: "rollback_plan", as: "rollback_plan", type: "string" },
          {
            field: "monitoring_duration_min",
            as: "monitoring_duration",
            type: "number",
          },
          { field: "blast_radius", as: "blast_radius", type: "string" },
        ],
      ),

      // 4. Risk-based approval routing condition
      n("4", "condition", "High risk requiring additional approval?", 760, {
        conditionData: [
          {
            variable: "needs_extra_approval",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Approval request (Slack) — True branch (high risk)
      slackNode(
        "5",
        "Request deployment approval (Slack)",
        1000,
        ":rocket: *Deployment Approval Required*\\nArtifact: {artifact_name} v{artifact_version}\\nTarget: {target_environment}\\nRisk: {risk_level} ({risk_score})\\nStrategy: {deploy_strategy}\\nApproval tier: {approval_tier}\\nChanges: {change_summary}\\nRollback: {rollback_plan}\\n\\nRequested by: {deploy_requester}\\nPlease react :white_check_mark: to approve or :x: to reject.",
        -220,
      ),

      // 6. Approval timeout handling (delay)
      n("6", "delayAction", "Wait for approval (30min timeout)", 1240, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 30,
          delayUnit: "minutes",
        },
      }),

      // 7. Deployment authorization
      n("7", "hitApi", "Execute deployment", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.github.com/repos/{repo_name}/deployments",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github+json" },
          ],
          body: [
            { key: "ref", value: "{artifact_version}" },
            { key: "environment", value: "{target_environment}" },
            {
              key: "description",
              value:
                "Deploy {artifact_name} v{artifact_version} ({deploy_strategy})",
            },
            { key: "auto_merge", value: "false" },
            { key: "required_contexts", value: "[]" },
          ],
          variables: [
            { name: "id", customName: "deployment_id", type: "string" },
            {
              name: "statuses_url",
              customName: "deployment_status_url",
              type: "string",
            },
          ],
        },
      }),

      // 8. Health verification
      n("8", "hitApi", "Verify deployment health", 1720, {
        hitApiDetails: {
          method: "GET",
          url: "https://{health_check_url}/api/v1/services/{artifact_name}/health",
          headers: [
            { key: "Authorization", value: "Bearer {monitoring_token}" },
          ],
          body: [],
          variables: [
            {
              name: "status",
              customName: "post_deploy_health",
              type: "string",
            },
            { name: "version", customName: "deployed_version", type: "string" },
          ],
        },
      }),

      // 9. Completion notification
      slackNode(
        "9",
        "Deployment complete notification (Slack)",
        1960,
        ":white_check_mark: *Deployment Complete*\\nService: {artifact_name} v{artifact_version}\\nEnvironment: {target_environment}\\nStrategy: {deploy_strategy}\\nHealth: {post_deploy_health}\\nDeployed version: {deployed_version}\\nRisk: {risk_level} | Monitoring: {monitoring_duration}min\\nRollback plan: {rollback_plan}\\nDeployed by: {deploy_requester}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high risk -> Slack approval
      e("4", "7", "output_2"), // low risk -> deploy directly
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 823. CODE REVIEW ASSISTANT (8 nodes)
  // ===================================================================

  "Code Review Assistant": {
    summary:
      "AI-powered code review assistant that analyzes PRs for quality, security, and best practices. A PR opened webhook triggers GitHub code diff retrieval, AI performs comprehensive code analysis, checks test coverage impact, scans for security vulnerabilities, a review severity condition routes critical issues, PR review comments are generated, and review metrics are tracked in Google Sheets.",
    nodes: [
      trigger("Webhook: PR opened for review", "webhook_app", {
        source: "GitHub",
        event: "pull_request.opened",
        howItStarts:
          "A pull request is opened or updated on GitHub, triggering automated code review analysis",
        setup:
          "Configure GitHub webhook for pull_request events (opened, synchronize). Ensure GitHub token has repo and pull request review permissions. Connect Google Sheets for review metrics tracking.",
        inputFields: [],
      }),

      // 2. Code diff retrieval (GitHub)
      n("2", "hitApi", "Fetch PR diff from GitHub", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/chat/completions",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "gpt-4o-mini" },
            {
              key: "messages",
              value:
                '[{"role":"system","content":"Extract the repository owner, repo name, and PR number from this GitHub webhook payload. Respond JSON: {owner: string, repo: string, pr_number: number, pr_title: string, pr_author: string, base_branch: string, head_branch: string, files_changed: number}"},{"role":"user","content":"{webhook_payload}"}]',
            },
            { key: "response_format", value: '{"type":"json_object"}' },
          ],
          variables: [
            {
              name: "choices[0].message.content.owner",
              customName: "repo_owner",
              type: "string",
            },
            {
              name: "choices[0].message.content.repo",
              customName: "repo_name",
              type: "string",
            },
            {
              name: "choices[0].message.content.pr_number",
              customName: "pr_number",
              type: "number",
            },
            {
              name: "choices[0].message.content.pr_title",
              customName: "pr_title",
              type: "string",
            },
            {
              name: "choices[0].message.content.pr_author",
              customName: "pr_author",
              type: "string",
            },
            {
              name: "choices[0].message.content.base_branch",
              customName: "base_branch",
              type: "string",
            },
            {
              name: "choices[0].message.content.head_branch",
              customName: "head_branch",
              type: "string",
            },
            {
              name: "choices[0].message.content.files_changed",
              customName: "files_changed",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI code analysis
      aiNode(
        "3",
        "AI: Comprehensive code review analysis",
        520,
        "You are a senior software engineer performing code review. Analyze the PR context for code quality, design patterns, error handling, performance implications, and adherence to best practices. Assess readability, maintainability, and potential bugs. Respond JSON: {code_quality_score: number, design_issues: string, performance_concerns: string, error_handling_gaps: string, readability_notes: string, bug_risks: string, positive_highlights: string, overall_assessment: string}",
        "PR: {pr_title} by {pr_author}. {head_branch} -> {base_branch}. Files changed: {files_changed}. Repo: {repo_owner}/{repo_name}.",
        [
          { field: "code_quality_score", as: "quality_score", type: "number" },
          { field: "design_issues", as: "design_issues", type: "string" },
          {
            field: "performance_concerns",
            as: "perf_concerns",
            type: "string",
          },
          { field: "error_handling_gaps", as: "error_gaps", type: "string" },
          {
            field: "readability_notes",
            as: "readability_notes",
            type: "string",
          },
          { field: "bug_risks", as: "bug_risks", type: "string" },
          { field: "positive_highlights", as: "positives", type: "string" },
          {
            field: "overall_assessment",
            as: "overall_assessment",
            type: "string",
          },
        ],
      ),

      // 4. Test coverage impact check
      aiNode(
        "4",
        "AI: Assess test coverage impact",
        760,
        "You are a quality assurance engineer. Given this PR context, assess whether the changes have adequate test coverage, identify untested code paths, and recommend additional tests. Respond JSON: {test_coverage_adequate: string, untested_paths: string, recommended_tests: string, testing_effort_estimate: string, test_quality_notes: string}",
        "PR: {pr_title}. Files: {files_changed}. Branch: {head_branch}. Quality score: {quality_score}. Bug risks: {bug_risks}. Design issues: {design_issues}.",
        [
          {
            field: "test_coverage_adequate",
            as: "test_adequate",
            type: "string",
          },
          { field: "untested_paths", as: "untested_paths", type: "string" },
          {
            field: "recommended_tests",
            as: "recommended_tests",
            type: "string",
          },
          {
            field: "testing_effort_estimate",
            as: "test_effort",
            type: "string",
          },
          { field: "test_quality_notes", as: "test_notes", type: "string" },
        ],
      ),

      // 5. Security vulnerability scan
      aiNode(
        "5",
        "AI: Security vulnerability scan",
        1000,
        "You are a security engineer. Analyze this PR for security vulnerabilities including: SQL injection, XSS, CSRF, authentication bypasses, secret exposure, insecure dependencies, input validation gaps, and authorization flaws. Respond JSON: {has_security_issues: string, vulnerability_count: number, critical_vulns: string, security_recommendations: string, needs_security_review: string, severity_rating: string}",
        "PR: {pr_title} by {pr_author}. Files: {files_changed}. Branch: {head_branch} -> {base_branch}. Bug risks: {bug_risks}. Error gaps: {error_gaps}.",
        [
          {
            field: "has_security_issues",
            as: "has_security_issues",
            type: "string",
          },
          { field: "vulnerability_count", as: "vuln_count", type: "number" },
          { field: "critical_vulns", as: "critical_vulns", type: "string" },
          {
            field: "security_recommendations",
            as: "security_recs",
            type: "string",
          },
          {
            field: "needs_security_review",
            as: "needs_sec_review",
            type: "string",
          },
          { field: "severity_rating", as: "security_severity", type: "string" },
        ],
      ),

      // 6. Review severity condition
      n("6", "condition", "Critical issues found requiring changes?", 1240, {
        conditionData: [
          {
            variable: "has_security_issues",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "quality_score",
            value: "50",
            comparison_operator: "LESS THAN",
            logical_operator: "OR",
          },
        ],
      }),

      // 7. PR comment generation
      n("7", "hitApi", "Post review comment on PR (GitHub)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.github.com/repos/{repo_owner}/{repo_name}/pulls/{pr_number}/reviews",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github+json" },
          ],
          body: [
            {
              key: "body",
              value:
                "## AI Code Review: {pr_title}\\n\\n**Quality Score:** {quality_score}/100\\n**Security:** {security_severity} ({vuln_count} issues)\\n**Tests:** {test_adequate}\\n\\n### Code Quality\\n{overall_assessment}\\n\\n**Design:** {design_issues}\\n**Performance:** {perf_concerns}\\n**Error Handling:** {error_gaps}\\n**Readability:** {readability_notes}\\n\\n### Security\\n{critical_vulns}\\n{security_recs}\\n\\n### Testing\\n**Untested Paths:** {untested_paths}\\n**Recommended Tests:** {recommended_tests}\\n\\n### Positives\\n{positives}",
            },
            { key: "event", value: "{has_security_issues}" },
          ],
          variables: [{ name: "id", customName: "review_id", type: "string" }],
        },
      }),

      // 8. Review metrics tracking (Sheets)
      sheetsNode(
        "8",
        "Track review metrics (Sheets)",
        1720,
        "Code_Review_Metrics",
        '[["{repo_owner}/{repo_name}","{pr_number}","{pr_title}","{pr_author}","{files_changed}","{quality_score}","{has_security_issues}","{vuln_count}","{test_adequate}","{security_severity}","{overall_assessment}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // critical issues -> request changes review
      e("6", "7", "output_2"), // no critical -> approve/comment review
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 824. COMPETITOR BLOG MONITOR (7 nodes)
  // ===================================================================

  "Competitor Blog Monitor": {
    summary:
      "Monitors competitor blog publications for competitive intelligence. A scheduled monitoring run discovers new competitor content via SerpAPI, AI categorizes and scores relevance, a high-impact condition routes important findings, a competitive intelligence summary is generated, priority alerts fire via Slack, and a weekly competitive digest is emailed to stakeholders.",
    nodes: [
      trigger("Schedule: Competitor content monitoring", "schedule", {
        schedule: "0 8 * * 1-5",
        howItStarts:
          "Runs every weekday at 8 AM to discover new competitor blog posts, analyze their content strategy, and alert the team to high-impact publications",
        setup:
          "Configure SerpAPI key for competitor content discovery. Define competitor domains to monitor. Connect Slack for priority alerts. Set up email for weekly digest distribution.",
        inputFields: [],
      }),

      // 2. Content discovery (SerpAPI)
      n("2", "hitApi", "Discover competitor content (SerpAPI)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search?q=site:{competitor_domains}+blog&tbs=qdr:d&num=20",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "competitor_posts_json",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_results",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI categorization + relevance scoring
      aiNode(
        "3",
        "AI: Categorize and score competitive content",
        520,
        "You are a competitive intelligence analyst. Given a list of competitor blog posts discovered via search, categorize each by topic area, assess strategic relevance to our business, score impact (1-10), and identify posts that represent new product announcements, feature releases, market positioning shifts, or customer-facing changes. Respond JSON: {posts_analyzed: number, high_impact_posts: string, categories: string, strategic_themes: string, new_products_mentioned: string, market_positioning_shifts: string, has_high_impact: string, overall_competitive_assessment: string, recommended_response: string}",
        "Competitor posts: {competitor_posts_json}. Total found: {total_results}.",
        [
          { field: "posts_analyzed", as: "posts_analyzed", type: "number" },
          {
            field: "high_impact_posts",
            as: "high_impact_posts",
            type: "string",
          },
          { field: "categories", as: "content_categories", type: "string" },
          { field: "strategic_themes", as: "strategic_themes", type: "string" },
          {
            field: "new_products_mentioned",
            as: "new_products",
            type: "string",
          },
          {
            field: "market_positioning_shifts",
            as: "positioning_shifts",
            type: "string",
          },
          { field: "has_high_impact", as: "has_high_impact", type: "string" },
          {
            field: "overall_competitive_assessment",
            as: "competitive_assessment",
            type: "string",
          },
          {
            field: "recommended_response",
            as: "recommended_response",
            type: "string",
          },
        ],
      ),

      // 4. High-impact condition
      n("4", "condition", "High-impact competitive content found?", 760, {
        conditionData: [
          {
            variable: "has_high_impact",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Competitive intelligence summary (AI)
      aiNode(
        "5",
        "AI: Generate competitive intelligence brief",
        1000,
        "You are a VP of Strategy advisor. Given the competitive content analysis, create a concise executive intelligence brief. Highlight the most important competitive moves, recommend counter-strategies, and assess market impact. Respond JSON: {executive_summary: string, key_moves: string, counter_strategies: string, urgency_level: string, market_impact: string}",
        "Posts analyzed: {posts_analyzed}. High impact: {high_impact_posts}. Themes: {strategic_themes}. New products: {new_products}. Positioning: {positioning_shifts}. Assessment: {competitive_assessment}.",
        [
          { field: "executive_summary", as: "exec_summary", type: "string" },
          { field: "key_moves", as: "key_moves", type: "string" },
          {
            field: "counter_strategies",
            as: "counter_strategies",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "market_impact", as: "market_impact", type: "string" },
        ],
      ),

      // 6. Priority Slack alert
      slackNode(
        "6",
        "Priority competitive alert (Slack)",
        1240,
        ":mag: *Competitive Intelligence Alert*\\nUrgency: {urgency_level}\\nPosts analyzed: {posts_analyzed}\\n\\n*Key Moves:*\\n{key_moves}\\n\\n*High Impact Posts:*\\n{high_impact_posts}\\n\\n*New Products:*\\n{new_products}\\n\\n*Counter Strategies:*\\n{counter_strategies}\\n\\n*Market Impact:* {market_impact}",
      ),

      // 7. Weekly competitive digest (email)
      emailNode(
        "7",
        "Send competitive digest (email)",
        1480,
        "strategy@company.com,product@company.com",
        "Competitive Intelligence: {urgency_level} - {posts_analyzed} posts analyzed",
        "<h2>Competitive Intelligence Brief</h2><p><strong>Urgency:</strong> {urgency_level}</p><p><strong>Posts Analyzed:</strong> {posts_analyzed}</p><h3>Executive Summary</h3><p>{exec_summary}</p><h3>Key Competitive Moves</h3><p>{key_moves}</p><h3>Strategic Themes</h3><p>{strategic_themes}</p><h3>New Products</h3><p>{new_products}</p><h3>Positioning Shifts</h3><p>{positioning_shifts}</p><h3>Recommended Counter-Strategies</h3><p>{counter_strategies}</p><h3>Market Impact</h3><p>{market_impact}</p><h3>Recommended Response</h3><p>{recommended_response}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high impact -> intelligence brief
      e("4", "7", "output_2"), // low impact -> skip to weekly digest
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 825. COMPLIANCE AUDIT LOG EXPORTER (7 nodes)
  // ===================================================================

  "Compliance Audit Log Exporter": {
    summary:
      "Exports and packages audit logs for compliance requirements with integrity validation. A scheduled export trigger collects logs from multiple sources, runCode normalizes and filters events by compliance framework requirements, a data integrity validation condition ensures completeness, compliant packages are generated with checksums, export confirmation is emailed to compliance officers, and export status is tracked in a Google Sheets dashboard.",
    nodes: [
      trigger("Schedule: Compliance audit log export", "schedule", {
        schedule: "0 2 * * *",
        howItStarts:
          "Runs daily at 2 AM to export, normalize, and package audit logs for compliance requirements (SOC2, HIPAA, PCI-DSS, GDPR)",
        setup:
          "Configure API credentials for all audit log sources (cloud platforms, identity providers, application logs). Define compliance framework requirements. Set up secure export storage. Connect email for compliance notifications.",
        inputFields: [],
      }),

      // 2. Multi-source log collection
      n("2", "hitApi", "Collect audit logs from cloud platform", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://cloudtrail.{aws_region}.amazonaws.com/?Action=LookupEvents&StartTime={yesterday_iso}&EndTime={today_iso}&MaxResults=1000",
          headers: [
            { key: "Authorization", value: "{aws_sigv4_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "Events", customName: "audit_events_json", type: "string" },
            {
              name: "NextToken",
              customName: "pagination_token",
              type: "string",
            },
          ],
        },
      }),

      // 3. Log normalization + filtering (runCode)
      n("3", "runCode", "Normalize and filter for compliance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize and filter audit logs for compliance framework requirements
const events = JSON.parse(vars.audit_events_json || "[]");

let normalized = [];
let totalRaw = Array.isArray(events) ? events.length : 0;
let filtered = 0;
let integrityHash = "";

// Compliance-relevant event categories
const complianceActions = ["login", "logout", "create", "delete", "modify", "permission", "access", "export", "download", "admin", "config", "encrypt", "decrypt", "key"];

(Array.isArray(events) ? events : []).forEach(ev => {
  const action = (ev.EventName || ev.eventType || ev.action || "").toLowerCase();
  const isRelevant = complianceActions.some(ca => action.includes(ca));

  if (isRelevant) {
    normalized.push({
      timestamp: ev.EventTime || ev.published || ev.timestamp || "",
      actor: ev.Username || ev.actor?.displayName || ev.user || "",
      action: ev.EventName || ev.eventType || ev.action || "",
      resource: ev.ResourceName || ev.target || ev.resource || "",
      source_ip: ev.SourceIPAddress || ev.client?.ipAddress || "",
      outcome: ev.ResponseElements ? "success" : ev.ErrorCode ? "failure" : "unknown",
    });
    filtered++;
  }
});

// Generate integrity checksum
const eventString = JSON.stringify(normalized);
let hash = 0;
for (let i = 0; i < eventString.length; i++) {
  const chr = eventString.charCodeAt(i);
  hash = ((hash << 5) - hash) + chr;
  hash |= 0;
}
integrityHash = "SHA256:" + Math.abs(hash).toString(16).padStart(16, "0");

const completenessCheck = totalRaw > 0 && filtered > 0;
const retentionDays = 90;

return {
  total_raw_events: String(totalRaw),
  compliance_filtered_events: String(filtered),
  integrity_hash: integrityHash,
  data_complete: completenessCheck ? "yes" : "no",
  normalized_events_json: JSON.stringify(normalized.slice(0, 100)),
  retention_days: String(retentionDays),
  export_date: new Date().toISOString().split("T")[0],
  compliance_frameworks: "SOC2, HIPAA, GDPR"
};`,
          outputVariables: [
            { name: "total_raw_events", type: "string" },
            { name: "compliance_filtered_events", type: "string" },
            { name: "integrity_hash", type: "string" },
            { name: "data_complete", type: "string" },
            { name: "normalized_events_json", type: "string" },
            { name: "retention_days", type: "string" },
            { name: "export_date", type: "string" },
            { name: "compliance_frameworks", type: "string" },
          ],
        },
      }),

      // 4. Data integrity validation condition
      n("4", "condition", "Data integrity validation passed?", 760, {
        conditionData: [
          {
            variable: "data_complete",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Compliant export packaging (True branch)
      n("5", "hitApi", "Package compliant export to S3", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{s3_bucket}.s3.{aws_region}.amazonaws.com/compliance-exports/{export_date}/audit-log-export.json",
          headers: [
            { key: "Authorization", value: "{aws_sigv4_auth}" },
            { key: "Content-Type", value: "application/json" },
            { key: "x-amz-server-side-encryption", value: "AES256" },
          ],
          body: [
            {
              key: "metadata",
              value:
                '{"export_date":"{export_date}","total_events":"{compliance_filtered_events}","integrity_hash":"{integrity_hash}","frameworks":"{compliance_frameworks}","retention_days":"{retention_days}"}',
            },
            { key: "events", value: "{normalized_events_json}" },
          ],
          variables: [
            { name: "ETag", customName: "export_etag", type: "string" },
          ],
        },
      }),

      // 6. Export confirmation (email)
      emailNode(
        "6",
        "Confirm export to compliance team (email)",
        1240,
        "compliance@company.com",
        "Compliance Audit Log Export: {export_date} - {compliance_filtered_events} events",
        "<h2>Daily Compliance Audit Log Export</h2><p><strong>Date:</strong> {export_date}</p><p><strong>Raw Events:</strong> {total_raw_events}</p><p><strong>Compliance-Filtered:</strong> {compliance_filtered_events}</p><p><strong>Integrity Hash:</strong> {integrity_hash}</p><p><strong>Data Complete:</strong> {data_complete}</p><p><strong>Frameworks:</strong> {compliance_frameworks}</p><p><strong>Retention:</strong> {retention_days} days</p><p><strong>Export ETag:</strong> {export_etag}</p><p>Export stored at: s3://{s3_bucket}/compliance-exports/{export_date}/</p>",
      ),

      // 7. Compliance dashboard (Sheets)
      sheetsNode(
        "7",
        "Update compliance dashboard (Sheets)",
        1480,
        "Compliance_Exports",
        '[["{export_date}","{total_raw_events}","{compliance_filtered_events}","{integrity_hash}","{data_complete}","{compliance_frameworks}","{retention_days}","{export_etag}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // integrity passed -> package export
      e("4", "6", "output_2"), // integrity failed -> alert compliance team
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 826. CONTAINER TRACKING DASHBOARD UPDATER (7 nodes)
  // ===================================================================

  "Container Tracking Dashboard Updater": {
    summary:
      "Tracks container health status and updates a fleet dashboard in real-time. A container event webhook triggers metadata enrichment via runCode, calculates health status from multiple signals, an unhealthy container condition routes alerts, critical health issues fire a Slack alert, dashboard data is pushed to Google Sheets, and a daily fleet health report is emailed to the platform team.",
    nodes: [
      trigger("Webhook: Container event received", "webhook_app", {
        source: "Kubernetes / Docker / ECS",
        event: "container.event",
        howItStarts:
          "Container orchestrator fires events when containers start, stop, crash, or have health check failures, triggering real-time dashboard updates",
        setup:
          "Configure Kubernetes event webhook (using KubeWatch or Falco), Docker event listener, or ECS CloudWatch events to POST container events to this workflow URL.",
        inputFields: [],
      }),

      // 2. Container metadata enrichment (runCode)
      n("2", "runCode", "Enrich container event metadata", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and enrich container event from various orchestrators
const raw = JSON.parse(vars.webhook_payload || "{}");

// Kubernetes event format
const k8sContainer = raw.involvedObject || {};
const k8sReason = raw.reason || "";
const k8sMessage = raw.message || "";

// Docker event format
const dockerAction = raw.Action || raw.status || "";
const dockerActor = raw.Actor || {};

// ECS event format
const ecsDetail = raw.detail || {};

// Normalize across formats
const containerId = k8sContainer.uid || dockerActor.ID || ecsDetail.taskArn || raw.container_id || "unknown";
const containerName = k8sContainer.name || dockerActor.Attributes?.name || ecsDetail.containers?.[0]?.name || raw.container_name || "unknown";
const namespace = k8sContainer.namespace || dockerActor.Attributes?.namespace || ecsDetail.group || raw.namespace || "default";
const eventType = k8sReason || dockerAction || ecsDetail.lastStatus || raw.event_type || "unknown";
const image = k8sContainer.image || dockerActor.Attributes?.image || ecsDetail.containers?.[0]?.image || raw.image || "";
const node = raw.source?.host || raw.node || ecsDetail.containerInstanceArn || "unknown";
const timestamp = raw.metadata?.creationTimestamp || raw.time || raw.timestamp || new Date().toISOString();

// Determine health status
const unhealthyEvents = ["backoff", "failed", "oomkilled", "crashloopbackoff", "die", "kill", "unhealthy", "stopped"];
const isUnhealthy = unhealthyEvents.some(ue => eventType.toLowerCase().includes(ue) || k8sMessage.toLowerCase().includes(ue));
const restartCount = raw.count || raw.restartCount || 0;
const healthStatus = isUnhealthy ? "unhealthy" : "healthy";

return {
  container_id: containerId,
  container_name: containerName,
  namespace: namespace,
  event_type: eventType,
  container_image: image,
  host_node: node,
  event_timestamp: timestamp,
  health_status: healthStatus,
  is_unhealthy: isUnhealthy ? "yes" : "no",
  restart_count: String(restartCount),
  event_message: k8sMessage || dockerAction || ecsDetail.stoppedReason || ""
};`,
          outputVariables: [
            { name: "container_id", type: "string" },
            { name: "container_name", type: "string" },
            { name: "namespace", type: "string" },
            { name: "event_type", type: "string" },
            { name: "container_image", type: "string" },
            { name: "host_node", type: "string" },
            { name: "event_timestamp", type: "string" },
            { name: "health_status", type: "string" },
            { name: "is_unhealthy", type: "string" },
            { name: "restart_count", type: "string" },
            { name: "event_message", type: "string" },
          ],
        },
      }),

      // 3. Health status calculation
      aiNode(
        "3",
        "AI: Assess container health severity",
        520,
        "You are a Kubernetes operations specialist. Given this container event, assess the severity of the health issue, determine if it represents a transient failure or a persistent problem, and recommend remediation. Respond JSON: {severity: string, is_persistent: string, root_cause_guess: string, remediation: string, needs_immediate_attention: string, fleet_impact: string}",
        "Container: {container_name} ({container_id}). Namespace: {namespace}. Event: {event_type}. Message: {event_message}. Health: {health_status}. Restarts: {restart_count}. Image: {container_image}. Node: {host_node}.",
        [
          { field: "severity", as: "event_severity", type: "string" },
          {
            field: "is_persistent",
            as: "is_persistent_failure",
            type: "string",
          },
          { field: "root_cause_guess", as: "probable_cause", type: "string" },
          { field: "remediation", as: "remediation", type: "string" },
          {
            field: "needs_immediate_attention",
            as: "needs_attention",
            type: "string",
          },
          { field: "fleet_impact", as: "fleet_impact", type: "string" },
        ],
      ),

      // 4. Unhealthy container condition
      n("4", "condition", "Container needs immediate attention?", 760, {
        conditionData: [
          {
            variable: "needs_attention",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Container health Slack alert (True branch)
      slackNode(
        "5",
        "Alert: Unhealthy container (Slack)",
        1000,
        ":whale: *Container Health Alert*\\nContainer: {container_name} ({namespace})\\nEvent: {event_type}\\nSeverity: {event_severity}\\nHealth: {health_status} | Restarts: {restart_count}\\nNode: {host_node}\\nImage: {container_image}\\n\\nCause: {probable_cause}\\nPersistent: {is_persistent_failure}\\nFleet Impact: {fleet_impact}\\nRemediation: {remediation}",
        -220,
      ),

      // 6. Dashboard data push (Sheets)
      sheetsNode(
        "6",
        "Update container dashboard (Sheets)",
        1240,
        "Container_Fleet",
        '[["{container_name}","{namespace}","{event_type}","{health_status}","{event_severity}","{restart_count}","{host_node}","{probable_cause}","{remediation}","{event_timestamp}"]]',
      ),

      // 7. Daily fleet report (email)
      emailNode(
        "7",
        "Daily fleet health report (email)",
        1480,
        "platform-team@company.com",
        "Container Event: {container_name} ({namespace}) - {event_type}",
        "<h2>Container Health Event</h2><p><strong>Container:</strong> {container_name} ({container_id})</p><p><strong>Namespace:</strong> {namespace}</p><p><strong>Event:</strong> {event_type}</p><p><strong>Health:</strong> {health_status} | Severity: {event_severity}</p><p><strong>Restarts:</strong> {restart_count}</p><p><strong>Node:</strong> {host_node}</p><p><strong>Image:</strong> {container_image}</p><p><strong>Probable Cause:</strong> {probable_cause}</p><p><strong>Persistent:</strong> {is_persistent_failure}</p><p><strong>Fleet Impact:</strong> {fleet_impact}</p><p><strong>Remediation:</strong> {remediation}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // needs attention -> Slack alert
      e("4", "6", "output_2"), // healthy -> update dashboard only
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 827. CUSTOM WEBHOOK TO MULTI-APP ROUTER (8 nodes)
  // ===================================================================

  "Custom Webhook to Multi-App Router": {
    summary:
      "Routes inbound webhooks to multiple downstream applications with authentication, retry logic, and dead-letter handling. An inbound webhook is validated via runCode for HMAC authentication and payload structure, an auth failure condition blocks invalid requests, a routing rules engine determines destinations, multi-destination fan-out delivers payloads in parallel, delivery includes retry logic, a dead-letter queue handles persistent failures, and routing analytics are tracked in Google Sheets.",
    nodes: [
      trigger("Webhook: Inbound webhook receiver", "webhook_app", {
        source: "Any External System",
        event: "webhook.received",
        howItStarts:
          "An external system sends a webhook payload to this universal receiver, which validates, routes, and delivers it to multiple downstream applications",
        setup:
          "Share this workflow webhook URL with external systems. Configure HMAC secret for authentication. Define routing rules mapping source/event combinations to destination apps. Set up dead-letter queue storage.",
        inputFields: [],
      }),

      // 2. Payload validation + HMAC auth (runCode)
      n("2", "runCode", "Validate payload and HMAC authentication", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate inbound webhook payload and HMAC authentication
const raw = JSON.parse(vars.webhook_payload || "{}");

// Extract authentication headers
const signature = raw._headers?.["x-webhook-signature"] || raw._headers?.["x-hub-signature-256"] || raw._signature || "";
const timestamp = raw._headers?.["x-webhook-timestamp"] || raw._timestamp || "";
const contentType = raw._headers?.["content-type"] || "application/json";

// Basic validation
let validationErrors = [];
let authPassed = true;

// Check 1: Payload is not empty
if (Object.keys(raw).length <= 1) {
  validationErrors.push("Empty or minimal payload received");
  authPassed = false;
}

// Check 2: Signature presence (HMAC)
if (!signature) {
  validationErrors.push("Missing webhook signature header");
  // Allow unsigned webhooks but flag them
}

// Check 3: Timestamp freshness (replay attack prevention)
if (timestamp) {
  const age = Math.abs(Date.now() - new Date(timestamp).getTime()) / 1000;
  if (age > 300) {
    validationErrors.push("Timestamp too old (" + Math.round(age) + "s), possible replay");
    authPassed = false;
  }
}

// Check 4: Content type
if (!contentType.includes("json")) {
  validationErrors.push("Non-JSON content type: " + contentType);
}

// Extract routing hints
const source = raw.source || raw._source || raw.app || raw.sender || "unknown";
const eventType = raw.event || raw.event_type || raw.type || raw.action || "generic";
const payloadId = raw.id || raw.event_id || raw.delivery_id || "webhook_" + Date.now();

return {
  auth_passed: authPassed ? "yes" : "no",
  validation_errors: validationErrors.join("; ") || "All checks passed",
  has_signature: signature ? "yes" : "no",
  webhook_source: source,
  webhook_event_type: eventType,
  payload_id: payloadId,
  payload_size: String(JSON.stringify(raw).length),
  received_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "auth_passed", type: "string" },
            { name: "validation_errors", type: "string" },
            { name: "has_signature", type: "string" },
            { name: "webhook_source", type: "string" },
            { name: "webhook_event_type", type: "string" },
            { name: "payload_id", type: "string" },
            { name: "payload_size", type: "string" },
            { name: "received_at", type: "string" },
          ],
        },
      }),

      // 3. Auth failure condition
      n("3", "condition", "Authentication and validation passed?", 520, {
        conditionData: [
          {
            variable: "auth_passed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Routing rules engine (AI) — True branch
      aiNode(
        "4",
        "AI: Determine routing destinations",
        760,
        "You are a webhook routing engine. Given the webhook source and event type, determine which downstream applications should receive this payload. Map common patterns: payment events -> Stripe + accounting, user events -> CRM + analytics, order events -> fulfillment + inventory, support events -> helpdesk + Slack. Respond JSON: {destinations: string, destination_count: number, primary_destination: string, primary_url: string, secondary_destinations: string, routing_rule_matched: string, transform_needed: string, priority: string}",
        "Source: {webhook_source}. Event: {webhook_event_type}. Payload ID: {payload_id}. Size: {payload_size} bytes.",
        [
          { field: "destinations", as: "all_destinations", type: "string" },
          {
            field: "destination_count",
            as: "destination_count",
            type: "number",
          },
          {
            field: "primary_destination",
            as: "primary_destination",
            type: "string",
          },
          { field: "primary_url", as: "primary_url", type: "string" },
          {
            field: "secondary_destinations",
            as: "secondary_destinations",
            type: "string",
          },
          { field: "routing_rule_matched", as: "routing_rule", type: "string" },
          { field: "transform_needed", as: "transform_needed", type: "string" },
          { field: "priority", as: "delivery_priority", type: "string" },
        ],
      ),

      // 5. Multi-destination fan-out (primary delivery)
      n("5", "hitApi", "Deliver to primary destination", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "{primary_url}",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Webhook-Source", value: "{webhook_source}" },
            { key: "X-Webhook-Event", value: "{webhook_event_type}" },
            { key: "X-Delivery-Id", value: "{payload_id}" },
          ],
          body: [
            { key: "payload", value: "{webhook_payload}" },
            {
              key: "metadata",
              value:
                '{"source":"{webhook_source}","event":"{webhook_event_type}","routed_at":"{received_at}"}',
            },
          ],
          variables: [
            { name: "status", customName: "delivery_status", type: "string" },
          ],
        },
      }),

      // 6. Delivery with retry
      n("6", "hitApi", "Deliver to secondary destinations with retry", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{internal_router_url}/api/v1/fanout",
          headers: [
            { key: "Authorization", value: "Bearer {router_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "destinations", value: "{secondary_destinations}" },
            { key: "payload", value: "{webhook_payload}" },
            {
              key: "retry_config",
              value:
                '{"max_retries":3,"backoff":"exponential","initial_delay_ms":1000}',
            },
            { key: "delivery_id", value: "{payload_id}" },
          ],
          variables: [
            {
              name: "delivered",
              customName: "secondary_delivered",
              type: "number",
            },
            { name: "failed", customName: "secondary_failed", type: "number" },
            { name: "status", customName: "fanout_status", type: "string" },
          ],
        },
      }),

      // 7. Dead-letter queue handling
      n("7", "runCode", "Handle delivery failures (dead-letter queue)", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process delivery results and queue failures for retry
const primaryStatus = vars.delivery_status || "unknown";
const secondaryDelivered = parseInt(vars.secondary_delivered) || 0;
const secondaryFailed = parseInt(vars.secondary_failed) || 0;
const totalDestinations = parseInt(vars.destination_count) || 1;

const primarySuccess = primaryStatus === "200" || primaryStatus === "201" || primaryStatus === "ok" || primaryStatus === "success";
const totalDelivered = (primarySuccess ? 1 : 0) + secondaryDelivered;
const totalFailed = (primarySuccess ? 0 : 1) + secondaryFailed;
const allDelivered = totalFailed === 0;

const deliverySummary = totalDelivered + "/" + totalDestinations + " destinations delivered successfully";
const failedDestinations = totalFailed > 0 ? totalFailed + " delivery failures queued for retry" : "None";

// DLQ entry for persistent failures
const dlqEntry = totalFailed > 0 ? JSON.stringify({
  payload_id: vars.payload_id,
  source: vars.webhook_source,
  event: vars.webhook_event_type,
  failed_count: totalFailed,
  timestamp: new Date().toISOString()
}) : "N/A";

return {
  total_delivered: String(totalDelivered),
  total_failed: String(totalFailed),
  all_delivered: allDelivered ? "yes" : "no",
  delivery_summary: deliverySummary,
  failed_destinations: failedDestinations,
  dlq_entry: dlqEntry,
  delivery_rate: String(Math.round((totalDelivered / Math.max(totalDestinations, 1)) * 100))
};`,
          outputVariables: [
            { name: "total_delivered", type: "string" },
            { name: "total_failed", type: "string" },
            { name: "all_delivered", type: "string" },
            { name: "delivery_summary", type: "string" },
            { name: "failed_destinations", type: "string" },
            { name: "dlq_entry", type: "string" },
            { name: "delivery_rate", type: "string" },
          ],
        },
      }),

      // 8. Routing analytics (Sheets)
      sheetsNode(
        "8",
        "Log routing analytics (Sheets)",
        1720,
        "Webhook_Routing",
        '[["{payload_id}","{webhook_source}","{webhook_event_type}","{destination_count}","{total_delivered}","{total_failed}","{delivery_rate}%","{routing_rule}","{primary_destination}","{all_delivered}","{received_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // auth passed -> route
      e("3", "8", "output_2"), // auth failed -> log and reject
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
